// ==============================================
// API Service with Authentication
// ==============================================

import axios, { AxiosInstance, AxiosError, isAxiosError } from 'axios';

// Allow individual requests to opt out of the global error toast for
// statuses that are expected in normal use (e.g. a 404 that just means
// "nothing here yet"). All other requests keep the default toast behavior.
declare module 'axios' {
  export interface AxiosRequestConfig {
    suppressErrorToast?: number[];
  }
}
import type { Package, PackageChange, Subscription, UsageSummary, MyTileUsage, AdminTileUsage, TileReconciliation, Invoice, PaymentMethod, CompanySettings, BilledPricesReport } from '../types/subscription';
import type { User } from '../types/auth';
import type {
  Device,
  DeviceStats,
  InventoryStats,
  CreateDeviceInput,
  UpdateDeviceInput,
  QRCodeLookupResponse,
  ClaimDeviceInput,
  DeviceEntitlement,
  DeviceAuditLog,
  DownloadHistoryItem,
  Screenshot,
  ScreenshotList,
  ScreenshotStorage,
} from '../types/device';
import type {
  StockProduct,
  StockItem,
  StockItemDetail,
  StockBuild,
  StockAllocation,
  InventoryStats as StockInventoryStats,
  ReceiveBatchResult,
  StockState,
  ProductCategory,
  IntakeParser,
  AttributeSchemaField,
} from '../types/inventory';
import type { HardwareOrder } from '../types/hardwareOrders';
import type {
  FeedbackDetail,
  FeedbackEvent,
  FeedbackListPage,
  FeedbackListParams,
  FeedbackStaffMember,
  FeedbackUpdateInput,
  FeedbackVisibility,
} from '../types/feedback';
import type {
  Department,
  DepartmentStatus,
  StaffMember,
  MyTimesheetEntry,
  TimesheetEntry,
  CreateTimesheetEntryInput,
  UpdateTimesheetEntryInput,
  DepartmentReportRow,
  StaffReportRow,
  MatrixReportRow,
} from '../types/timesheets';

import type * as CP from '../features/crewPapers/types';
import type { DiagnosticReport, DiagnosticShareLink } from '../types/diagnostics';

import { resolveApiUrl } from '../lib/api-url';
import { parsePackHeaders } from '../utils/entryPack';

/** Roles a Scout sign-in rule is set for (docs/core/architecture/SCOUT_MYPOLARIS_SIGN_IN.md). */
export type ScoutSignInRole = 'user' | 'tester' | 'admin' | 'super_admin';
export interface ScoutSignInSettings {
  days_by_role: Record<ScoutSignInRole, number | null>;
}

// Scout onboarding funnel (mirrors vortex-api FunnelRow)
/** A Scout Gateway release (vortex-api routes/gatewaySoftware.ts). */
export interface GatewayRelease {
  id: string;
  version: string;
  commit: string | null;
  size: number;
  channel: 'test' | 'early' | 'stable' | 'withdrawn';
  rollout_percent: number;
  paused: boolean;
  paused_reason: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}
export interface GatewayFleetVersion { version: string | null; channel: 'test' | 'early' | 'stable'; count: number }
export interface GatewayReport { release: string | null; version: string | null; result: 'installed' | 'kept' | 'rolled_back' | 'failed'; detail: string | null; at: string }
/** Remote access to an SG (vortex-api routes/gatewayRemote.ts). */
export interface GatewayMember { user_id: string; email: string | null; name: string | null; role: 'crew' | 'fleet'; until: string | null; added_at: string; expired?: boolean }
export interface GatewayJoinRequest { user_id: string; email: string | null; name: string | null; at: string }
export interface SharedGateway { device_id: string; serial_number: string; name: string | null; role: 'crew' | 'fleet'; until: string | null }
export interface GatewayRemoteUsage { month: string; bytes: number; cap_bytes: number | null }
/** A view-only guest link: no account, one device, until the owner's end date. */
export interface GatewayGuest {
  id: string; name: string; created: string; link_expires: string; until: string; opened_at: string | null;
  state: 'waiting' | 'open' | 'locked' | 'expired' | 'ended';
}
export interface GatewayGuestLink { id: string; pin: string; name: string; until: string; link_expires: string; url: string }
export interface GatewayRemoteAction { at: string; user: string; who: string | null; role: string; method: string; path: string; status: number }
/** What the owner sees about their SG's software. */
export interface GatewaySoftware {
  serial: string;
  channel: 'test' | 'early' | 'stable';
  version: string | null;
  update: { id: string; version: string; notes: string | null } | null;
  last: GatewayReport | null;
  reports: GatewayReport[];
}

export type OnboardingFunnelSegment = 'all' | 'no_account' | 'no_plan' | 'stalled' | 'complete' | 'not_booted' | 'in_transit' | 'anomaly';
export type OnboardingStepKey = 'antenna' | 'wifi' | 'update' | 'signin' | 'plan' | 'care';

export interface OnboardingStepCell {
  status: 'started' | 'verified' | 'stalled' | 'bypassed';
  occurred_at: string;
  received_at: string;
  position: { lat: number; lon: number; accuracy?: number | null } | null;
  details: Record<string, unknown> | null;
}

/** Which door the device-signin page shows, and whose boat it greets. */
export interface DeviceSigninContext {
  success: boolean;
  session_status: 'pending';
  serial_number: string;
  vessel_name: string | null;
  masked_email: string;
  account_state: 'new' | 'has_password' | 'no_password';
  social_providers: string[];
  at_limit: boolean;
  plan_name: string | null;
  max_devices: number | null;
}

/** A Scout offered for replacement in activate's device_limit_reached response. */
export interface DeviceSigninLimitScout {
  id: string;
  name: string | null;
  serial_number: string;
  status: string;
  last_seen_at: string | null;
}

/** One pass through the guide. A reset starts a new one; old runs are kept. */
export interface OnboardingRun {
  run_id: string;
  started_at: string;
  last_event_at: string;
  completed: boolean;
  events: (OnboardingStepCell & { step: OnboardingStepKey; run_id: string })[];
}

export interface OnboardingFunnelRow {
  device_id: string;
  serial_number: string;
  /** Hardware identity and the build record it resolves to. */
  wifi_mac_address: string | null;
  /** When the unit first reached the internet, and anything odd about that. */
  first_contact_at: string | null;
  inventory_anomaly: 'live_before_shipping' | 'no_allocation' | 'recipient_mismatch' | null;
  stock_item_id: string | null;
  stock_state: string | null;
  batch_tag: string | null;
  stock_attributes: { imei1?: string; model?: string; manufacture_date?: string } | null;
  name: string | null;
  status: string;
  inventory_status: string;
  user_email: string | null;
  has_active_plan: boolean;
  package_name: string | null;
  completed_at: string | null;
  reset_pending: boolean;
  /** Across all runs — the latest run may be a boot-time phantom (wifi + update only). */
  plan_ever_verified: boolean;
  /** Step 6 was entered by an admin on the customer's say-so, not sent by the Scout. */
  care_recorded_by_admin: boolean;
  /** Server-computed: signed in, plan verified in some run, not complete, no reset queued. */
  admin_acceptance_eligible: boolean;
  /** Admin-recorded step 6 only: the customer's estimated tap time (from the Scout's own traffic), or null when only the admin's clock is known. */
  admin_acceptance_estimated_at: string | null;
  run_id: string | null;
  steps: Partial<Record<OnboardingStepKey, OnboardingStepCell>>;
  last_event_at: string | null;
  last_heartbeat_at: string | null;
  current_step: string | null;
  last_position: { lat: number; lon: number; accuracy?: number | null; timestamp?: string } | null;
  shipped_at: string | null;
}

export interface FleetMapMarker {
  deviceId: string;
  deviceName: string | null;
  deviceType: string;
  deviceStatus: string;
  lastSeenAt: string | null;
  lastKnownPosition: { lat: number; lon: number; accuracy?: number; timestamp: string } | null;
  userId: string | null;
  userName: string | null;
  userEmail: string | null;
  userRole: string | null;
  packageName: string | null;
  packageSlug: string | null;
}
import { useToastStore } from '../store/toastStore';
const API_BASE_URL = resolveApiUrl();
const API_TIMEOUT = parseInt(import.meta.env.VITE_API_TIMEOUT || '30000', 10);
// File downloads get a much longer leash than JSON calls — bulk-imported
// manuals run 15+ MB, and 30s reads as "Network error" on slower links.
const BLOB_TIMEOUT = 300000;
/** Statuses the crew-request pages show inline (see crewLinkView and the calls below it). */
const CREW_QUIET = [400, 401, 403, 404, 409, 413, 415, 429, 500, 503];

// ---- Admin › Weather ----
export interface WeatherCatalogModel {
  id: string;
  runId: string;
  hours: number[];
  regions: string[];
  products: string[];
  publishedAt: string | null;
  encoderVersion: string | null;
}
export type WeatherRunOutcome = 'done' | 'running' | 'queued' | 'unfinished';
export interface WeatherRunSummary {
  model: string;
  runId: string;
  kicks: number;
  byStatus: Record<string, number>;
  starts: number;
  firstTs: number;
  lastTs: number;
  outcome: WeatherRunOutcome;
  /** This run's ingests that did work, oldest first (weather/<model>/<run>/ingest-log.json). */
  attempts?: WeatherIngestAttempt[];
}
/** One ingest of a run: `running` until it ends; the seconds are null until then. */
export interface WeatherIngestAttempt {
  startedAt: string;
  finishedAt: string | null;
  mode: string;
  trigger: string;
  status: string;
  wallSecs: number | null;
  fetchSecs: number | null;
  encodeSecs: number | null;
  uploadSecs: number | null;
  hydrateSecs: number | null;
}
export interface WeatherKick { kind: 'kick'; ts: number; model: string; runId: string; status: string; httpStatus: number | null; trigger: string }
export interface WeatherOverview {
  success: boolean;
  generatedAt: number;
  from: number;
  to: number;
  catalog: { ok: true; defaultModel: string | null; models: WeatherCatalogModel[] } | { ok: false; error: string };
  logs:
    | { configured: false; message: string }
    | { configured: true; error: string }
    | {
        configured: true;
        error?: undefined;
        kicks: number;
        runs: WeatherRunSummary[];
        stalled: { model: string; unfinished: number }[];
        lastKick: Record<string, WeatherKick>;
        deploys: number[];
        errors: { error: string; count: number }[];
        maxBusyMin: number;
        maxQueued: number;
        lastBusyTs: number | null;
        /** vortex-cdn's answers to the ingest's catalog updates; null if that log could not be read. */
        publishes?: WeatherPublishSummary | null;
      };
}
export interface WeatherPublishSummary {
  total: number;
  accepted: number;
  refused: number;
  byStatus: Record<string, number>;
  lastAcceptedTs: number | null;
  lastRefusedTs: number | null;
  blocked: boolean;
}
export interface WeatherAvailabilityResult {
  success: boolean;
  model: string;
  run: string;
  region: string;
  inCatalog: boolean;
  keys: number;
  availability: { expected: number; present: number; products: { product: string; present: number; missingHours: number[] }[] } | null;
}
export interface WeatherTriggerParams { model: 'ecmwf' | 'gfs' | 'icon'; mode?: 'auto' | 'refetch' | 'republish'; date?: string; cycle?: string }
/** One tile size of one run's worldwide tiles (vortex-weather src/cdn/tile_coverage.rs). */
export interface WeatherTileSize {
  sizeDeg: number;
  tiles: number;
  expected: number;
  present: number;
  percent: number;
  missingHours: number[];
  faultedTiles: number;
  complete: boolean;
  completedAt: string | null;
  verifiedAt: string | null;
}
export interface WeatherTileRun {
  model: string;
  run: string;
  worldwide: boolean;
  updatedAt: string | null;
  hours: number;
  runComplete: boolean;
  pendingSteps: number;
  sizes: WeatherTileSize[];
}
export interface WeatherTilesResult {
  success: boolean;
  models: { model: string; runs: WeatherTileRun[]; error?: string }[];
}

// ---- Admin › Routing (the passage-planning engine, vortex-routing) ----
export type RoutingModel = 'ecmwf0p25' | 'gfs025' | 'icon025';
export type RoutingKind = 'route' | 'evaluate' | 'window';
export type RoutingJobState = 'queued' | 'running' | 'done' | 'failed';
export interface RoutingForm {
  kind: RoutingKind;
  label?: string;
  start: [number, number];
  end: [number, number];
  /** Waypoints to pass through, in order. */
  via?: [number, number][];
  departMs?: number;
  window?: { fromMs: number; toMs: number; intervalH: number };
  models: RoutingModel[];
  resolution: 'quick' | 'standard' | 'fine';
  limits: { maxTwsKt: number; maxGustKt: number | null; maxHsM: number | null };
  motoring: { enabled: boolean; speedKt: number };
  currents: 'zero' | 'forecast';
  /** The boat's polar (from adminRoutingPolar); without it the demonstration polar. */
  polar?: RoutingPolar;
}
/** A boat's polar as the engine reads it: speeds [twa index][tws index], knots. */
export interface RoutingPolar { id: string; name: string; twsKt: number[]; twaDeg: number[]; stw: number[][] }
/** What the API read from a polar file (or a library polar): the completed 0–180° table,
 *  the layout in words, every fill as a sentence, and what to check (`line` and `column`
 *  from 1 where known). */
export interface RoutingPolarImport {
  polar: RoutingPolar | null;
  format: string;
  readAs?: string;
  notes?: string[];
  source?: { format: string; cells: number; twsRange: [number, number]; twaRange: [number, number] };
  issues: { level: 'error' | 'warning'; message: string; line?: number; column?: number }[];
}
/** A boat in the polar library. */
export interface PolarLibraryEntry { id: string; name: string; source: string; twsRange: [number, number]; twaRange: [number, number] }
export interface RoutingJobStatus {
  success?: boolean;
  id: string;
  state: RoutingJobState;
  stage?: string;
  ahead?: number | null;
  error?: string | null;
  cached?: boolean;
}
/** Who asked for a run: where from (admin, skipper, planner, check), their account and a name. */
export interface RoutingOrigin { source: string; userId?: string; who?: string; email?: string }
/** What the engine reports while a run waits or runs. */
export interface RoutingLive {
  stage?: string;
  progress?: { done: number; total: number; unit: string } | number | null;
  etaSec?: number | null;
  ahead?: number | null;
  stages?: { stage: string; startedMs: number | null }[];
}
/** One run on Admin › Routing, as GET /api/admin/routing/overview lists it. */
export interface RoutingConsoleJob {
  id: string;
  state: RoutingJobState;
  kind: RoutingKind | 'replay' | '';
  label: string | null;
  submittedMs: number;
  startedMs?: number;
  finishedMs?: number;
  /** It runs on a container of its own, not the shared engine. */
  own: boolean;
  attempts: number;
  error?: string;
  origin?: RoutingOrigin;
  /** `engine` (shared) or `job-<id>` (its own). */
  container: string;
  doId: string;
  live?: RoutingLive;
}
export interface RoutingHealth {
  engineVersion: string;
  /** The shared engine's container: running/healthy (awake), stopped, stopping … */
  shared: { state: string; doId: string };
  ownLimit: number;
  ownInUse: number;
  maxInstances: number;
  forecasts: { model: string; runId: string; publishedAt?: string }[];
  forecastError?: string;
}
export interface RoutingRunCost { cpuSec: number; memGiBSec: number; diskGBSec: number; usd: number; basis: 'measured' | 'estimate' }
export type RoutingCosts =
  | { available: true; sinceMs: number; jobs: Record<string, RoutingRunCost>; idleUsd: number; totalUsd: number }
  | { available: false; reason: string };
/** Why a delete was refused: what still reads the run. */
export interface RoutingInUse { departureChecks: number; commentaries: number }
export type LatLon = [number, number];
export interface RoutingLeg { from: LatLon; to: LatLon; departMs: number; arriveMs: number; headingDeg: number; distanceNm: number; motoring: boolean; manoeuvre: 'tack' | 'gybe' | null }
export interface RoutingRoute { legs: RoutingLeg[]; departMs: number; arriveMs: number; distanceNm: number; tacks: number; gybes: number; motoringHours: number; forecastId: string }
export type RoutingStatus = 'VALID_WITHIN_SUPPLIED_DATA' | 'NO_FEASIBLE_ROUTE_FOUND' | 'INSUFFICIENT_GEOGRAPHIC_DATA' | 'INSUFFICIENT_WEATHER_DATA' | 'COMPUTATION_LIMIT_REACHED';
export interface RoutingSearch { status: RoutingStatus; message: string; route: RoutingRoute | null; alternatives: RoutingRoute[] }
export interface RoutingCheck { id: string; state: 'PASS' | 'FAIL' | 'NOT_EVALUATED' | 'INSUFFICIENT_DATA' | 'DISCREPANCY'; findings: { severity: string; message: string }[] }
export interface RoutingValidation { checks: RoutingCheck[]; acceptance: RoutingStatus; replayArriveMs: number | null }
export interface RoutingReplay { forecastId: string; status: RoutingStatus; feasible: boolean; route: RoutingRoute | null; message: string }
export interface RoutingCandidate { id: string; fromForecast: string; rank: number; route: RoutingRoute; replays: Record<string, RoutingReplay>; feasibleUnderAll: boolean; coveredBy: string[]; shortOf: string[]; worstArriveMs: number | null; meanArriveMs: number | null }
export interface RoutingDeparture { departMs: number; runs: { forecastId: string; search: RoutingSearch; cause: string; causeDetail: string }[]; candidates: RoutingCandidate[]; compromise: number | null; refined: boolean; sharedWaveSource: boolean }
export interface RoutingResult {
  id: string;
  engineVersion: string;
  kind: RoutingKind;
  forecasts: { model: string; runId: string }[];
  shoreline: string;
  computeMs: number;
  result: { search: RoutingSearch; validation: RoutingValidation | null } | RoutingDeparture | { departures: RoutingDeparture[]; wallMs: number };
}

// ---- Admin › Departure planner › Test fleet requests (vortex-api routes/adminRouterTesters.ts) ----
export interface RouterTesterRequest {
  id: string;
  name: string;
  email: string;
  boatMake: string;
  boatModel: string;
  boatLengthM: number;
  passage: string;
  dates: string;
  heardFrom: string | null;
  status: 'new' | 'invited';
  invitedAt: string | null;
  invitedBy: string | null;
  createdAt: string;
}

// ---- Admin › Departure planner (vortex-api departure-watches) ----
export type DepartureWatchStatus = 'active' | 'paused' | 'ended';
export type DepartureAssessmentState = 'scanning' | 'scanned' | 'drafted' | 'pending_review' | 'approved' | 'sent' | 'held' | 'failed';
/** A watch as the form sends it. `ownerEmail` (admins only) names the owning account; left out, the admin owns it. */
/** A watch's boat-and-crew settings, as the API answers them (defaults filled in). */
export interface PassageBoat { lwlM: number | null; displacementKg: number | null; experience: 'coastal' | 'offshore' | 'ocean'; watchSystem: 2 | 3 }
export interface PassagePerformance {
  overallPct: number; upwindPct: number | null; reachingPct: number | null; downwindPct: number | null; nightPct: number;
  lightAir: 'drift' | 'motor'; waveLoss: { enabled: boolean; sensitivity: 'light' | 'normal' | 'heavy' };
}
/** The angles are apparent wind (vortex-api lib/watchSettings.ts); fromTrueWind: saved with true-wind angles before 17 Oct 2026. */
export interface PassageSailing { tackMin: number; gybeMin: number; minAwaDeg: number; maxAwaDeg: number; fromTrueWind?: boolean }
export interface PassageMotoring { triggerKt: number; headwindLossKtPer10: number; burnLph: number | null; usableL: number | null; reservePct: number; maxHours: number | null }
export interface PassageComfort {
  maxTwsKt: number; maxGustKt: number | null; maxHsM: number | null; maxBeatHours: number | null; maxHardHoursInRow: number | null;
  avoidSteepSeas: boolean; avoidDarkArrival: boolean; avoidSqualls: boolean;
}

export interface DepartureWatchForm {
  label: string;
  vesselName: string;
  email: string;
  ownerEmail?: string;
  start: LatLon;
  end: LatLon;
  via: LatLon[];
  departureTz: string;
  /** True: the zone above was chosen. False or absent: the API sets it from the start (nearest major port). */
  departureTzManual?: boolean;
  /** ISO. */
  windowFrom: string;
  windowDays: number;
  intervalH: number;
  models: RoutingModel[];
  polar: RoutingPolar | null;
  motoring: { enabled: boolean; speedKt: number } & Partial<PassageMotoring>;
  limits: { maxTwsKt: number; maxGustKt: number | null; maxHsM: number | null };
  /** The boat-and-crew settings (passage package contract C); absent = unchanged on save. */
  performance?: Partial<PassagePerformance>;
  sailing?: Partial<PassageSailing>;
  comfort?: Partial<PassageComfort>;
  currents: boolean;
  /** "When a forecast ends before you arrive"; absent = longest. */
  tailFill?: 'ecmwf' | 'gfs' | 'strict' | 'longest' | 'climatology';
  /** Boat and crew for the engine's ratings (sea state, wellbeing); null = unknown. */
  boat?: ({ loaM: number | null; crew: number | null } & Partial<PassageBoat>) | null;
  /** Motoring burn and usable fuel, for the ratings' fuel estimate; null = unknown. */
  fuel?: { burnLph: number | null; usableL: number | null } | null;
  /** Local hours at departureTz. */
  sendHours: number[];
  requiresApproval: boolean;
}
export interface DepartureWatch extends Omit<DepartureWatchForm, 'ownerEmail'> {
  id: string;
  status: DepartureWatchStatus;
  lastAssessedAt: string | null;
  createdAt: string;
  /** The next email slot, ISO; null unless active (and with a polar). */
  nextSendAt: string | null;
  /** No polar: the watch is not assessed until one is added. */
  needsPolar?: boolean;
  /** Why it is not assessed yet, in a line ("Add your boat's polar to start."), or null. */
  setupLine?: string | null;
  ownerUserId?: string;
  ownerEmail?: string | null;
  createdBy?: string | null;
  updatedAt?: string;
  /** The last calibration from a sailed passage, or null. */
  calibration?: { suggestedPct: number; sentence?: string } | null;
}
export interface DepartureAssessment {
  id: string;
  watchId: string;
  cycleAt: string;
  state: DepartureAssessmentState;
  /** `manual`: made by "Assess now" (cycleAt is when it was asked for). */
  trigger?: 'scheduled' | 'manual';
  emailSubject: string | null;
  emailBody: string | null;
  summary: unknown;
  sentAt: string | null;
  forecastRuns: Record<string, string>;
  routingJobId: string | null;
  checkResult: unknown;
  reviewedBy: string | null;
  reviewedAt: string | null;
  error: string | null;
  createdAt: string;
  updatedAt: string;
}

// ---- Admin › Update Checks › Live monitor ----
export interface OtaLiveRequest {
  kind: 'request';
  id: string;
  ts: number;
  deviceId: string;
  method: string;
  path: string;
  host: string;
  status: number | null;
  outcome: string | null;
  ms: number | null;
  ip: string | null;
  country: string | null;
  city: string | null;
  ua: string;
  rayId: string | null;
  channelHeader: string | null;
  channel: 'platform' | 'stable';
  app: string | null;
  monitor: boolean;
  probe: boolean;
}
export interface OtaLiveReportApp {
  app: string; pkg: string;
  installed: string | null; installedCode: number | null;
  available: string | null; availableCode: number | null;
  action: string | null; error: unknown;
}
export interface OtaLiveReport {
  kind: 'report';
  id: string;
  ts: number;
  receivedTs: number;
  deviceId: string;
  vessel: string | null;
  result: string;
  error: string | null;
  apps: OtaLiveReportApp[];
  launcher: string | null;
  ssid: string | null;
  model: string | null;
  android: string | null;
  channelHint: 'platform' | 'stable' | null;
  lat: number | null;
  lon: number | null;
}
export interface OtaLiveManifest {
  channel: 'stable' | 'platform';
  updatedAt: string | null;
  fetchedAt: number;
  apps: Record<string, { pkg: string; versionName: string; versionCode: number; sizeBytes: number; sha256: string }>;
}

/** GET /api/tiles/pass — who the tile front door counts, and the sources the pass opens. */
export interface TilePass { pass: string; expires_at: string; sources: string[] }

class ApiService {
  private client: AxiosInstance;

  /**
   * Super-admin "view as" subject, or null for normal operation.
   *
   * Held on the client rather than passed through every nav call: the
   * Navigation Library makes dozens of requests and threading a parameter
   * through each is how one gets missed — and a missed one would silently
   * read the superadmin's own library while the banner claimed otherwise,
   * which is worse than not having the feature.
   *
   * The server decides whether the caller may actually do this; sending the
   * header from a non-superadmin achieves nothing.
   */
  private viewAsUserId: string | null = null;

  /** Start or stop acting as another user. Affects Navigation Library calls only. */
  setViewAs(userId: string | null): void {
    this.viewAsUserId = userId;
  }

  getViewAs(): string | null {
    return this.viewAsUserId;
  }

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: API_TIMEOUT,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Scoped deliberately to /api/nav: the subject applies to the Navigation
    // Library and nothing else, so an active view-as cannot quietly follow the
    // superadmin into billing, devices or their own account settings.
    this.client.interceptors.request.use((config) => {
      const url = config.url ?? '';
      if (this.viewAsUserId && url.startsWith('/api/nav')) {
        config.headers = config.headers ?? {};
        (config.headers as Record<string, string>)['x-view-as-user'] = this.viewAsUserId;
      }
      return config;
    });

    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError<{ error?: string; message?: string }>) => {
        const status = error.response?.status;
        const body = error.response?.data;
        const msg = body?.error || body?.message || error.message || 'Something went wrong';

        const suppressed =
          status !== undefined &&
          (error.config?.suppressErrorToast ?? []).includes(status);

        if (status === 401) {
          console.warn('[API] Session expired or not authenticated');
        } else if (status === 403 && (body as { code?: string } | undefined)?.code === 'ADMIN_REQUIRED') {
          // Role transition, not a request failure: the session was swapped to
          // a non-admin (magic-link sign-in) while admin queries were still
          // polling. The guards already handle the UX; a toast per in-flight
          // poll just puts a "Forbidden" storm on screen.
          console.warn('[API] Admin call as non-admin (session changed) — suppressing toast');
        } else if (suppressed) {
          // Expected condition for this request; caller handles it silently.
        } else if (status && status >= 400) {
          useToastStore.getState().addToast({
            type: 'error',
            title: status >= 500 ? 'Server error' : 'Request failed',
            message: msg,
          });
        } else if (error.code === 'ECONNABORTED' || !error.response) {
          useToastStore.getState().addToast({
            type: 'error',
            title: 'Network error',
            message: 'Unable to reach the server. Check your connection.',
          });
        }

        return Promise.reject(error);
      }
    );
  }

  // ==========================================
  // User Endpoints
  // ==========================================

  async updateUiPreferences(patch: { pageSizes?: Record<string, number> }): Promise<{ pageSizes?: Record<string, number> }> {
    const response = await this.client.put('/api/users/me/ui-preferences', patch);
    return response.data.ui_preferences ?? {};
  }

  async getAdminMenuConfig(): Promise<import('../config/adminMenuCatalog').AdminMenuConfig | null> {
    const response = await this.client.get('/api/admin/menu-config');
    return response.data.data ?? null;
  }

  async saveAdminMenuConfig(
    config: import('../config/adminMenuCatalog').AdminMenuConfig,
  ): Promise<import('../config/adminMenuCatalog').AdminMenuConfig> {
    const response = await this.client.put('/api/admin/menu-config', config);
    return response.data.data;
  }

  /** Set a password on an account that has never had one (link-only sign-in). */
  async setFirstPassword(newPassword: string): Promise<void> {
    await this.client.post('/api/users/me/set-password', { newPassword });
  }

  /** Remember that the customer declined the backup-password suggestion. */
  async dismissPasswordPrompt(): Promise<void> {
    await this.client.post('/api/users/me/dismiss-password-prompt', {});
  }

  /**
   * A tile pass for the tile front door: the signed-in person's plan, counted under the
   * portal; signed out (or a plan that opens nothing), the public one.
   */
  async getTilePass(): Promise<TilePass> {
    try {
      const res = await this.client.get('/api/tiles/pass', { params: { app: 'portal' }, suppressErrorToast: [401, 403] });
      return res.data.data;
    } catch (err) {
      const status = (err as AxiosError).response?.status;
      if (status !== 401 && status !== 403) throw err;
      const res = await this.client.get('/api/tiles/pass/public', { params: { app: 'portal' } });
      return res.data.data;
    }
  }

  async getProfile(): Promise<User> {
    const response = await this.client.get('/api/users/me');
    return response.data.user ?? response.data;
  }

  async updateProfile(data: Partial<User>): Promise<User> {
    const response = await this.client.put('/api/users/me', data);
    return response.data;
  }

  async getMySubscription(): Promise<Subscription | null> {
    const response = await this.client.get('/api/users/me/subscription');
    return response.data;
  }

  /** Downloads page actions — retry a failed pack, refresh an expiring one, or download an expired one again (same primitive). */
  async downloadRequestAgain(id: string): Promise<void> { await this.client.post(`/api/users/me/downloads/${id}/request-again`, {}, { suppressErrorToast: [404, 409] }); }
  async downloadRemove(id: string): Promise<void> { await this.client.delete(`/api/users/me/downloads/${id}`, { suppressErrorToast: [404, 409] }); }
  async downloadRenameArea(id: string, name: string): Promise<void> { await this.client.patch(`/api/users/me/downloads/${id}/area`, { name }, { suppressErrorToast: [400, 403, 404] }); }

  /** Admin › Map Tiles › Esri check: what Esri billed each day against what we account for. */
  async adminGetTileReconciliation(days = 14): Promise<TileReconciliation> {
    const response = await this.client.get('/api/admin/tiles/reconciliation', { params: { days } });
    return response.data.data;
  }

  /** Move one device onto the tile front door, or back to direct (Admin › Devices). */
  async adminSetTileRoute(deviceId: string, route: 'direct' | 'front_door'): Promise<{ device_id: string; route: string }> {
    const response = await this.client.put(`/api/admin/tiles/devices/${encodeURIComponent(deviceId)}/route`, { route });
    return response.data.data;
  }

  /** Admin › Map Tiles: every customer's tiles for the month, used and fetched. */
  async adminGetTileUsage(month: string): Promise<AdminTileUsage> {
    const response = await this.client.get('/api/admin/tiles/usage', { params: { month } });
    return response.data.data;
  }

  /** This month's map tiles (UTC calendar month), by source, viewed live and downloaded. */
  /** Open a one-off Stripe payment for a top-up pack of the customer's plan; answers its URL. */
  async buyTokenPack(packageId: string, tokenPackId: string, currency: string): Promise<{ url?: string; checkout_url?: string }> {
    const response = await this.client.post('/api/subscriptions/checkout', { packageId, tokenPackId, currency });
    return response.data.data;
  }

  async getMyTileUsage(month?: string): Promise<MyTileUsage> {
    const response = await this.client.get('/api/tiles/usage', { params: month ? { month } : undefined });
    return response.data.data;
  }

  async getMyUsage(): Promise<UsageSummary> {
    const response = await this.client.get('/api/users/me/usage');
    return response.data;
  }

  async getMyDownloads(params?: {
    page?: number;
    limit?: number;
    deviceId?: string;
    status?: string;
  }): Promise<{
    data: DownloadHistoryItem[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
  }> {
    const response = await this.client.get('/api/users/me/downloads', { params });
    return response.data;
  }

  // ---- MyPolaris Screenshot Library ----

  async getScreenshots(params?: {
    device?: string;
    favourite?: boolean;
    q?: string;
    page?: number;
    limit?: number;
  }): Promise<ScreenshotList> {
    const response = await this.client.get('/api/screenshots', {
      params: {
        device: params?.device,
        favourite: params?.favourite ? 'true' : undefined,
        q: params?.q || undefined,
        page: params?.page,
        limit: params?.limit,
      },
    });
    return response.data.data;
  }

  async getScreenshotStorage(): Promise<ScreenshotStorage> {
    const response = await this.client.get('/api/screenshots/storage');
    return response.data.data;
  }

  async updateScreenshot(
    id: string,
    patch: { caption?: string | null; favourite?: boolean },
  ): Promise<Screenshot> {
    const response = await this.client.patch(`/api/screenshots/${id}`, patch);
    return response.data.data;
  }

  async deleteScreenshot(id: string): Promise<void> {
    await this.client.delete(`/api/screenshots/${id}`);
  }

  async restoreScreenshot(id: string): Promise<void> {
    await this.client.post(`/api/screenshots/${id}/restore`, {});
  }

  async batchDeleteScreenshots(ids: string[]): Promise<number> {
    const response = await this.client.post('/api/screenshots/batch-delete', { ids });
    return response.data.data.deleted;
  }

  /** Blob URLs for <img>/<a> tags — session cookie rides along via CORS credentials. */
  screenshotThumbUrl(id: string): string {
    return `${API_BASE_URL}/api/screenshots/${id}/thumb`;
  }

  screenshotImageUrl(id: string, download = false): string {
    return `${API_BASE_URL}/api/screenshots/${id}/image${download ? '?download=1' : ''}`;
  }

  async getMyOrders(params?: { limit?: number; offset?: number }): Promise<{ orders: any[]; total: number }> {
    const response = await this.client.get('/api/users/me/orders', { params });
    return response.data;
  }

  // ==========================================
  // Email Preferences
  // ==========================================

  async getEmailPreferences(): Promise<{
    order_notifications: boolean;
    subscription_notifications: boolean;
    billing_notifications: boolean;
    usage_notifications: boolean;
    marketing_emails: boolean;
  }> {
    const response = await this.client.get('/api/users/me/email-preferences');
    return response.data.data;
  }

  async updateEmailPreferences(preferences: {
    order_notifications?: boolean;
    subscription_notifications?: boolean;
    billing_notifications?: boolean;
    usage_notifications?: boolean;
    marketing_emails?: boolean;
  }): Promise<{
    order_notifications: boolean;
    subscription_notifications: boolean;
    billing_notifications: boolean;
    usage_notifications: boolean;
    marketing_emails: boolean;
  }> {
    const response = await this.client.put('/api/users/me/email-preferences', preferences);
    return response.data.data;
  }

  // ==========================================
  // Invoice Endpoints
  // ==========================================

  async getMyInvoices(params?: { limit?: number; page?: number; status?: string }): Promise<{
    data: Invoice[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
  }> {
    const response = await this.client.get('/api/invoices', { params });
    return response.data;
  }

  async getInvoice(invoiceId: string): Promise<Invoice> {
    const response = await this.client.get(`/api/invoices/${invoiceId}`);
    return response.data.data;
  }

  // ==========================================
  // Package Endpoints (Public)
  // ==========================================

  async getPackages(): Promise<Package[]> {
    const response = await this.client.get('/api/packages');
    return response.data.packages || response.data;
  }

  async getStandardFeatures(): Promise<string[]> {
    const response = await this.client.get('/api/packages/standard-features');
    return response.data.data || [];
  }

  async getPackageBySlug(slug: string): Promise<Package> {
    const response = await this.client.get(`/api/packages/${slug}`);
    return response.data.package || response.data;
  }

  // ==========================================
  // Subscription Endpoints
  // ==========================================

  async createCheckoutSession(packageId: string, paymentMethodId?: string, promoCode?: string): Promise<{
    sessionId?: string;
    url?: string;
    devMode?: boolean;
    created?: boolean;
    subscription?: Subscription;
    message?: string;
  }> {
    console.log('[API] ========== createCheckoutSession ==========');
    console.log('[API] packageId:', packageId);
    console.log('[API] paymentMethodId:', paymentMethodId ?? '(none — will use hosted checkout)');
    if (promoCode) console.log('[API] promoCode:', promoCode);

    try {
      const body: Record<string, string> = { packageId };
      if (paymentMethodId) body.paymentMethodId = paymentMethodId;
      if (promoCode) body.promoCode = promoCode;

      const response = await this.client.post('/api/subscriptions/checkout', body);
      console.log('[API] Response status:', response.status);
      console.log('[API] Response data:', JSON.stringify(response.data, null, 2));
      return response.data.data;
    } catch (error: any) {
      console.error('[API] createCheckoutSession error:', error.message);
      console.error('[API] Error response:', error.response?.data);
      console.error('[API] Error status:', error.response?.status);
      throw error;
    }
  }

  /**
   * The subscription. Its `usage` is the service's camelCase summary, not a
   * UsageSummary: read token figures from getMyUsage(), as every page does.
   */
  async getCurrentSubscription(): Promise<{
    subscription: Subscription | null;
    usage: Record<string, unknown>;
  }> {
    const response = await this.client.get('/api/subscriptions/current');
    return response.data.data;
  }

  async getSubscriptionUsage(): Promise<{
    records: any[];
    summary: UsageSummary;
  }> {
    const response = await this.client.get('/api/subscriptions/usage');
    return response.data.data;
  }

  async changeSubscription(packageId: string): Promise<void> {
    await this.client.post('/api/subscriptions/change', { packageId });
  }

  async cancelSubscription(): Promise<Subscription> {
    const response = await this.client.post('/api/subscriptions/cancel');
    return response.data.data;
  }

  async reactivateSubscription(): Promise<Subscription> {
    const response = await this.client.post('/api/subscriptions/reactivate');
    return response.data.data;
  }

  async renewSubscription(): Promise<Subscription> {
    const response = await this.client.post('/api/subscriptions/renew');
    return response.data.data;
  }

  async validatePromoCode(promoCode: string, packageId?: string): Promise<{
    valid: boolean;
    promo_id?: string;
    promo_name?: string;
    amount_off?: number | null;
    percent_off?: number | null;
    currency?: string | null;
    duration?: string;
    duration_in_months?: number | null;
    reason?: string;
  }> {
    const body: Record<string, string> = { promoCode };
    if (packageId) body.packageId = packageId;
    const response = await this.client.post('/api/subscriptions/validate-promo', body);
    return response.data.data;
  }

  async getBillingPortalUrl(): Promise<string> {
    const response = await this.client.post('/api/subscriptions/portal');
    return response.data.data.url;
  }

  // ==========================================
  // Payment Methods
  // ==========================================

  async getPaymentMethods(): Promise<PaymentMethod[]> {
    try {
      const response = await this.client.get('/api/users/me/payment-methods', {
        suppressErrorToast: [400],
      });
      return response.data;
    } catch (error) {
      // 400 here means the account has no Stripe customer yet, i.e. simply
      // no saved payment methods. Render the empty state, not an error.
      if (isAxiosError(error) && error.response?.status === 400) return [];
      throw error;
    }
  }

  async createSetupIntent(): Promise<{ clientSecret: string }> {
    const response = await this.client.post('/api/users/me/payment-methods/setup-intent');
    return response.data;
  }

  async addPaymentMethod(paymentMethodId: string): Promise<PaymentMethod> {
    const response = await this.client.post('/api/users/me/payment-methods', { paymentMethodId });
    return response.data;
  }

  async setDefaultPaymentMethod(paymentMethodId: string): Promise<void> {
    await this.client.put(`/api/users/me/payment-methods/${paymentMethodId}/default`);
  }

  async deletePaymentMethod(paymentMethodId: string): Promise<void> {
    await this.client.delete(`/api/users/me/payment-methods/${paymentMethodId}`);
  }

  // ==========================================
  // Billing Portal
  // ==========================================

  async createBillingPortalSession(): Promise<{ url: string }> {
    const response = await this.client.post('/api/subscriptions/portal');
    return response.data.data;
  }

  // ==========================================
  // Orders
  // ==========================================

  async calculateOrderPricing(data: {
    tileCount: number;
    includeNauticalOverlay?: boolean;
  }): Promise<{
    package: Package | null;
    subscriptionId: string | null;
    tokensAvailable: number;
    tokensFromQuota: number;
    tokensOverage: number;
    baseFee: number;
    tilesPrice: number;
    total: number;
    isAuthenticated: boolean;
  }> {
    const response = await this.client.post('/api/orders/calculate', data);
    return response.data.data;
  }

  async createOrder(data: {
    aoiGeometry: any;
    aoiBounds: number[];
    minZoom: number;
    maxZoom: number;
    tileCount: number;
    includeNauticalOverlay?: boolean;
    imagerySource?: string;
    notificationEmail?: string;
  }): Promise<{ order: any; requiresPayment: boolean; clientSecret?: string }> {
    const response = await this.client.post('/api/orders', data);
    return response.data.data;
  }

  async confirmOrder(orderId: string, paymentIntentId: string): Promise<any> {
    const response = await this.client.post(`/api/orders/${orderId}/confirm`, { paymentIntentId });
    return response.data.data;
  }

  async getOrders(params?: { limit?: number; page?: number; status?: string }): Promise<{
    orders: any[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
  }> {
    const response = await this.client.get('/api/orders', { params });
    return response.data;
  }

  async getRecentOrders(limit = 5): Promise<any[]> {
    const response = await this.client.get('/api/orders/recent', { params: { limit } });
    return response.data.data;
  }

  async getOrderStats(): Promise<{
    totalOrders: number;
    completedOrders: number;
    totalTilesGenerated: number;
    totalSpent: number;
  }> {
    const response = await this.client.get('/api/orders/stats');
    return response.data.data;
  }

  async getOrder(orderId: string): Promise<any> {
    const response = await this.client.get(`/api/orders/${orderId}`);
    return response.data.data;
  }

  async getOrderStatus(orderId: string): Promise<{
    id: string;
    status: string;
    progress: number;
    totalTiles: number;
    successfulTiles: number;
    failedTiles: number;
    downloadUrl: string | null;
    downloadExpiresAt: string | null;
    errorMessage: string | null;
  }> {
    const response = await this.client.get(`/api/orders/${orderId}/status`);
    return response.data.data;
  }

  async cancelOrder(orderId: string): Promise<any> {
    const response = await this.client.post(`/api/orders/${orderId}/cancel`);
    return response.data.data;
  }

  // ==========================================
  // Admin Endpoints
  // ==========================================

  async adminGetPackages(): Promise<Package[]> {
    const response = await this.client.get('/api/packages/admin/all');
    return response.data.packages || [];
  }

  async adminCreatePackage(data: Partial<Package>): Promise<Package> {
    const response = await this.client.post('/api/packages/admin', data);
    return response.data.package || response.data;
  }

  /** `reason` is the admin's optional note, kept in the plan's history. */
  async adminUpdatePackage(id: string, data: Partial<Package>, reason?: string): Promise<Package> {
    const response = await this.client.put(`/api/packages/admin/${id}`, reason ? { ...data, reason } : data);
    return response.data.package || response.data;
  }

  async adminGetPackageChanges(id: string): Promise<PackageChange[]> {
    const response = await this.client.get(`/api/packages/admin/${id}/changes`);
    return response.data.changes || [];
  }

  /** Active subscriptions per plan. */
  async adminGetPackageSubscriberCounts(): Promise<Record<string, number>> {
    const response = await this.client.get('/api/packages/admin/stats');
    const rows: { packageId: string; subscribers: number }[] = response.data.stats?.revenueByPackage || [];
    return Object.fromEntries(rows.map((r) => [r.packageId, r.subscribers]));
  }

  async adminDeletePackage(id: string): Promise<void> {
    await this.client.delete(`/api/packages/admin/${id}`);
  }

  async adminGetDefaultPackage(): Promise<Package | null> {
    const response = await this.client.get('/api/packages/admin/default');
    return response.data.package || null;
  }

  async adminSetDefaultPackage(id: string): Promise<{ newDefault: Package; previousDefault: Package | null }> {
    const response = await this.client.put(`/api/packages/admin/default/${id}`);
    return response.data;
  }

  async adminGetStandardFeatures(): Promise<string[]> {
    const response = await this.client.get('/api/packages/admin/standard-features');
    return response.data.data || [];
  }

  async adminUpdateStandardFeatures(features: string[]): Promise<string[]> {
    const response = await this.client.put('/api/packages/admin/standard-features', { features });
    return response.data.data || [];
  }

  async adminGetUsers(params?: {
    limit?: number;
    offset?: number;
    role?: string;
    status?: string;
    package_id?: string;
    account_origin?: string;
    /** Is this the plan they pay for (usersListState USERS_BILLING). */
    billing?: string;
    search?: string;
  }): Promise<{ users: User[]; total: number }> {
    const response = await this.client.get('/api/admin/users', { params });
    // Backend returns { success, data: [...users], pagination: { total, ... } }
    // Transform to { users, total } format expected by frontend
    const result = response.data;
    return {
      users: result.data || [],
      total: result.pagination?.total || 0,
    };
  }

  async adminUpdateUser(id: string, data: Partial<User>): Promise<User> {
    const response = await this.client.put(`/api/admin/users/${id}`, data);
    return response.data;
  }

  async adminChangeSubscription(userId: string, packageId: string | null, overrideNote?: string | null): Promise<any> {
    const response = await this.client.put(`/api/admin/users/${userId}/subscription`, { package_id: packageId, override_note: overrideNote ?? null });
    return response.data;
  }

  /** Record that the customer's current plan is given on purpose (docs/core/architecture/PLAN_OVERRIDES.md). */
  async adminMarkPlanOverride(userId: string, overrideNote: string | null): Promise<void> {
    await this.client.put(`/api/admin/users/${userId}/subscription/override`, { override_note: overrideNote });
  }

  /**
   * Read, from Stripe, which plan each Stripe-billed subscription is charged
   * for and Stripe's own status for it. Changes no plan.
   */
  async adminRefreshBilledPrices(): Promise<BilledPricesReport> {
    const response = await this.client.post('/api/admin/billing/refresh-billed-prices');
    return response.data.data;
  }

  async adminUpdateSubscriptionExpiry(userId: string, currentPeriodEnd: string): Promise<any> {
    const response = await this.client.put(`/api/admin/users/${userId}/subscription/expiry`, { current_period_end: currentPeriodEnd });
    return response.data;
  }

  async adminDeleteUser(id: string): Promise<{ success: boolean; message: string }> {
    const response = await this.client.delete(`/api/admin/users/${id}`);
    return response.data;
  }

  async adminGetUserDevices(userId: string): Promise<{ success: boolean; data: Device[] }> {
    const response = await this.client.get(`/api/admin/users/${userId}/devices`);
    return response.data;
  }

  /** A customer's token lots (admin). */
  async adminGetUserTokens(userId: string) {
    const response = await this.client.get(`/api/admin/users/${userId}/tokens`);
    return response.data.data;
  }

  /** Quote or refund a bought pack, or give goodwill tokens (admin; refund and give need a reason). */
  async adminTokenAction(
    userId: string,
    body: { action: 'quote'; lot_id: string } | { action: 'refund'; lot_id: string; reason: string } | { action: 'give'; tokens: number; reason: string },
  ) {
    const response = await this.client.post(`/api/admin/users/${userId}/tokens`, body);
    return response.data.data;
  }

  async adminGetUserStripeBilling(userId: string): Promise<{
    success: boolean;
    data?: {
      customer_name: string | null;
      customer_email: string | null;
      stripe_customer_id: string;
      stripe_dashboard_customer_url: string;
      totals_by_currency: { currency: string; total_paid_cents: number; charge_count: number }[];
      truncated: boolean;
      subscriptions: Array<{
        id: string;
        product_name: string;
        status: string;
        frequency_label: string;
        cancel_summary: string | null;
      }>;
      payments: Array<{
        charge_id: string;
        amount_cents: number;
        currency: string;
        description: string | null;
        created: number;
        status: string;
        payment_method_label: string;
        stripe_invoice_id: string | null;
        hosted_invoice_url: string | null;
        stripe_dashboard_invoice_url: string | null;
      }>;
    };
    error?: string;
  }> {
    const response = await this.client.get(`/api/admin/users/${userId}/stripe-billing`);
    return response.data;
  }

  async adminDeleteDevice(deviceId: string): Promise<{ success: boolean; message: string }> {
    const response = await this.client.delete(`/api/devices/${deviceId}`);
    return response.data;
  }

  async adminGetDashboard(): Promise<{
    users: { total: number; active: number; suspended: number; admins: number };
    packages: { totalPackages: number; activePackages: number; totalSubscribers: number; revenueByPackage: { packageId: string; name: string; subscribers: number; monthlyRevenue: number }[] };
    monthlyRevenue: number;
    collectedLast30Days: number;
    recentOrders: any[];
    userGrowth: { date: string; count: number }[];
    packageExpiry: { packageId: string; name: string; activeTotal: number; expiring30: number; expiring60: number; expiring90: number; expiringBeyond: number }[];
  }> {
    const response = await this.client.get('/api/admin/dashboard');
    return response.data.data ?? response.data;
  }

  async adminGetExpiringSubscribers(params: {
    packageId: string;
    band: 'active' | '0_30' | '31_60' | '61_90' | '90_plus';
  }): Promise<{ userId: string; name: string | null; email: string; expiresAt: string | null }[]> {
    const response = await this.client.get('/api/admin/dashboard/expiring-subscribers', { params });
    return response.data.data || [];
  }

  // ==========================================
  // Admin - Company Settings (for Invoicing)
  // ==========================================

  async adminGetCompanySettings(): Promise<CompanySettings | null> {
    const response = await this.client.get('/api/invoices/admin/settings');
    return response.data.data;
  }

  async adminUpdateCompanySettings(data: Partial<CompanySettings>): Promise<CompanySettings> {
    const response = await this.client.put('/api/invoices/admin/settings', data);
    return response.data.data;
  }

  /** How long a Scout keeps its owner signed in to MyPolaris, per role (null = never signs them out). */
  async adminGetScoutSignIn(): Promise<ScoutSignInSettings> {
    const response = await this.client.get('/api/admin/onboarding/scout-sign-in');
    return response.data.data;
  }

  async adminSaveScoutSignIn(settings: ScoutSignInSettings): Promise<ScoutSignInSettings> {
    const response = await this.client.put('/api/admin/onboarding/scout-sign-in', settings);
    return response.data.data;
  }

  // ==========================================
  // Admin - API Keys Management
  // ==========================================

  async adminGetApiKeys(): Promise<Record<string, string>> {
    const response = await this.client.get('/api/admin/settings/keys');
    return response.data.keys ?? {};
  }

  async adminUpdateApiKeys(keys: Record<string, string>): Promise<Record<string, string>> {
    const response = await this.client.put('/api/admin/settings/keys', { keys });
    return response.data.keys ?? {};
  }

  // ==========================================
  // Admin - Invoice Management
  // ==========================================

  async adminGetInvoices(params?: {
    limit?: number;
    page?: number;
    status?: string;
    userId?: string;
  }): Promise<{
    data: Invoice[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
  }> {
    const response = await this.client.get('/api/invoices/admin/list', { params });
    return response.data;
  }

  async adminVoidInvoice(invoiceId: string): Promise<Invoice> {
    const response = await this.client.post(`/api/invoices/admin/${invoiceId}/void`);
    return response.data.data;
  }

  async adminResendInvoiceEmail(invoiceId: string): Promise<void> {
    await this.client.post(`/api/invoices/admin/${invoiceId}/resend`);
  }

  // ==========================================
  // Legacy Job Endpoints (backwards compatibility)
  // ==========================================

  async startJob(jobData: {
    bounds: { north: number; south: number; east: number; west: number };
    minZoom: number;
    maxZoom: number;
    includeNautical?: boolean;
    imagerySource?: string;
  }): Promise<{ jobId: string }> {
    const response = await this.client.post('/api/start-job', jobData);
    return response.data;
  }

  async getJobStatus(jobId: string): Promise<{
    status: string;
    progress?: number;
    downloadUrl?: string;
    error?: string;
  }> {
    const response = await this.client.get(`/api/job/${jobId}/status`);
    return response.data;
  }

  // ==========================================
  // Scout onboarding (magic-link device sign-in)
  // ==========================================

  /**
   * Phone-side confirm after the magic-link verify established the cookie
   * session: claims the Scout for the signed-in user and releases the
   * registration code to the polling device.
   */
  async confirmDeviceSignin(sessionId: string): Promise<{
    success: boolean;
    device_id: string;
    serial_number: string;
  }> {
    const response = await this.client.post('/api/onboarding/signin/confirm', {
      session_id: sessionId,
    });
    return response.data;
  }

  /**
   * What the device-signin page renders: which door (account state), whose
   * boat, and the at-limit early warning. Side-effect-free — safe to call on
   * page load despite mail-scanner prefetch.
   */
  async getDeviceSigninContext(sessionId: string): Promise<DeviceSigninContext> {
    const response = await this.client.get('/api/onboarding/signin/context', {
      params: { das: sessionId },
    });
    return response.data;
  }

  /**
   * The atomic activation gate: password proof (existing accounts) or password
   * creation (new / credential-less accounts), magic-link consumption, claim,
   * and code release — one call. A 409 device_limit_reached response carries
   * the user's Scouts for the swap step, with the cookie session established.
   */
  async activateDeviceSignin(input: {
    session_id: string;
    token: string;
    password?: string;
    new_password?: string;
  }): Promise<{ success: boolean; device_id: string; serial_number: string }> {
    const response = await this.client.post('/api/onboarding/signin/activate', input);
    return response.data;
  }

  /** Follow-up to activate's 409: replace an existing Scout with the new one. */
  async swapDeviceSignin(input: {
    session_id: string;
    replace_device_id: string;
  }): Promise<{ success: boolean; device_id: string; serial_number: string; replaced_device_id: string }> {
    const response = await this.client.post('/api/onboarding/signin/swap', input);
    return response.data;
  }

  /**
   * "Forgot your password?" on the device-signin page. The page only holds the
   * masked email, so the server requests the reset from the session's address.
   */
  async forgotDeviceSigninPassword(sessionId: string): Promise<{ success: boolean }> {
    const response = await this.client.post('/api/onboarding/signin/forgot-password', {
      session_id: sessionId,
    });
    return response.data;
  }

  /**
   * "Send login code" on the login page: the API emails a 6-digit code when the
   * address has an account, and answers the same either way.
   */
  async sendLoginCode(email: string): Promise<{ success: boolean; data: { expiresInSeconds: number } }> {
    // The login page says what went wrong in place, so no toast on top of it.
    const response = await this.client.post('/api/login-code/send', { email }, { suppressErrorToast: [400, 429, 500] });
    return response.data;
  }

  /** The code typed in: a right one sets the session cookie on this screen. */
  async verifyLoginCode(email: string, code: string): Promise<{ success: boolean }> {
    const response = await this.client.post('/api/login-code/verify', { email, code }, { suppressErrorToast: [400, 403, 429, 500] });
    return response.data;
  }

  async adminGetOnboardingDevices(params: {
    segment?: OnboardingFunnelSegment;
    search?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<{ rows: OnboardingFunnelRow[]; total: number; segment: OnboardingFunnelSegment }> {
    const response = await this.client.get('/api/admin/onboarding/devices', { params });
    return response.data.data;
  }

  /**
   * Full onboarding history for one device, newest run first. The funnel shows
   * only the current run, so this is how an earlier attempt is recovered after
   * an out-of-box reset replaced it.
   */
  async adminGetOnboardingRuns(deviceId: string): Promise<OnboardingRun[]> {
    const response = await this.client.get(`/api/admin/onboarding/devices/${deviceId}/runs`);
    return response.data.data.runs;
  }

  /** Queue the two-phase out-of-box reset (executes when the device acks). */
  async adminResetOnboardingDevice(deviceId: string): Promise<{
    command_id: string;
    status: string;
    already_pending?: boolean;
  }> {
    const response = await this.client.post(`/api/admin/onboarding/devices/${deviceId}/reset`, {});
    return response.data.data;
  }

  /**
   * Withdraw a queued out-of-box reset. A reset only lands when the Scout is
   * online, and it forgets the device's saved networks — so anything queued
   * against a just-reset unit waits and fires mid-walk, wiping the customer's
   * progress. This is the way out of that.
   */
  async adminCancelOnboardingReset(deviceId: string): Promise<{ cancelled: number }> {
    const response = await this.client.delete(`/api/admin/onboarding/devices/${deviceId}/reset`);
    return response.data.data;
  }

  /**
   * Erase a device row — clears bench/test units out of the funnel. Cascades
   * its entitlements, commands, packs and history; leaves the stock/build
   * record and the audit trail. NOT a decommission: a Scout that is still
   * online re-provisions itself from its build record on next contact.
   */
  async adminDeleteOnboardingDevice(deviceId: string): Promise<{ deleted: string; serial_number: string }> {
    const response = await this.client.delete(`/api/admin/onboarding/devices/${deviceId}`);
    return response.data.data;
  }

  /**
   * Record step 6 (care & terms) on the admin's say-so — for a Scout whose
   * customer accepted the terms on-device but whose care/verified event never
   * reached the platform (launcher event-queue bug, vortexnav-client PR #636).
   * Server refuses (409) unless the customer signed in and step 5 is verified.
   */
  async adminRecordOnboardingAcceptance(deviceId: string, note?: string): Promise<{ run_id: string; completed_at: string }> {
    const response = await this.client.post(`/api/admin/onboarding/devices/${deviceId}/accept-care`, note ? { note } : {});
    return response.data.data;
  }

  /** Today's offline-verifiable support unlock code for a stuck Scout. */
  async adminGetOnboardingUnlockCode(deviceId: string): Promise<{
    code: string;
    valid_for_utc_day: string;
    serial_number: string;
  }> {
    const response = await this.client.post(`/api/admin/onboarding/devices/${deviceId}/unlock-code`, {});
    return response.data.data;
  }

  // ==========================================
  // Device Endpoints
  // ==========================================

  async createDevice(name: string, deviceType: string): Promise<{
    registration_code: string;
    registration_code_expires_at: string;
  }> {
    const response = await this.client.post('/api/devices', {
      name,
      device_type: deviceType,
    });
    return response.data;
  }

  async getMyDevices(): Promise<{ devices: Device[]; total: number }> {
    const response = await this.client.get('/api/devices');
    return response.data;
  }

  async getDevice(id: string): Promise<Device> {
    const response = await this.client.get(`/api/devices/${id}`);
    return response.data;
  }

  // Scout Gateway software (docs/core/architecture/SCOUT_GATEWAY_UPDATES.md)
  async getGatewaySoftware(id: string): Promise<GatewaySoftware> {
    const response = await this.client.get(`/api/gateway-software/devices/${encodeURIComponent(id)}`);
    return response.data.data;
  }

  /** Whether the owner's SG is connected for remote access (docs/core/architecture/SCOUT_GATEWAY_REMOTE.md). */
  async getGatewayRemoteStatus(id: string): Promise<{ online: boolean }> {
    const response = await this.client.get(`/api/gateway-remote/${encodeURIComponent(id)}/status`);
    return response.data;
  }

  /** SGs shared with me, as crew or fleet operator. */
  async getSharedGateways(): Promise<{ devices: SharedGateway[] }> {
    const response = await this.client.get('/api/gateway-remote/shared');
    return response.data;
  }

  /** Someone scanned SG's QR code on board: ask its owner to let them in. */
  async joinGateway(id: string, code: string): Promise<{ status: 'requested' } | { status: 'member'; role: 'owner' | 'fleet' | 'crew' }> {
    const response = await this.client.post(`/api/gateway-remote/${encodeURIComponent(id)}/join`, { code });
    return response.data;
  }

  async getGatewayPeople(id: string): Promise<{ members: GatewayMember[]; requests: GatewayJoinRequest[] }> {
    const response = await this.client.get(`/api/gateway-remote/${encodeURIComponent(id)}/people`);
    return response.data;
  }

  async approveGatewayPerson(id: string, person: { user_id: string; role: 'crew' | 'fleet'; until?: string | null }): Promise<GatewayMember> {
    const response = await this.client.post(`/api/gateway-remote/${encodeURIComponent(id)}/people`, person);
    return response.data;
  }

  async removeGatewayPerson(id: string, userId: string): Promise<void> {
    await this.client.delete(`/api/gateway-remote/${encodeURIComponent(id)}/people/${encodeURIComponent(userId)}`);
  }

  /** The owner: remote data over the boat's link this month, and the cap. */
  async getGatewayRemoteUsage(id: string): Promise<GatewayRemoteUsage> {
    const response = await this.client.get(`/api/gateway-remote/${encodeURIComponent(id)}/usage`);
    return response.data;
  }

  async setGatewayRemoteCap(id: string, capMb: number | null): Promise<GatewayRemoteUsage> {
    const response = await this.client.put(`/api/gateway-remote/${encodeURIComponent(id)}/usage`, { cap_mb: capMb });
    return response.data;
  }

  /** The owner: the last changes made to the SG remotely. */
  async getGatewayRemoteLog(id: string): Promise<{ actions: GatewayRemoteAction[] }> {
    const response = await this.client.get(`/api/gateway-remote/${encodeURIComponent(id)}/log`);
    return response.data;
  }

  /** The owner: view-only guest links (a link and a PIN; no account). */
  async getGatewayGuests(id: string): Promise<{ guests: GatewayGuest[] }> {
    const response = await this.client.get(`/api/gateway-remote/${encodeURIComponent(id)}/guests`);
    return response.data;
  }

  async createGatewayGuest(id: string, guest: { name: string; days: number }): Promise<GatewayGuestLink> {
    const response = await this.client.post(`/api/gateway-remote/${encodeURIComponent(id)}/guests`, guest);
    return response.data;
  }

  async removeGatewayGuest(id: string, guestId: string): Promise<void> {
    await this.client.delete(`/api/gateway-remote/${encodeURIComponent(id)}/guests/${encodeURIComponent(guestId)}`);
  }

  /** Where MyPolaris opens the SG app, carried to the boat by the relay. */
  gatewayRemoteAppUrl(id: string): string {
    return `${API_BASE_URL}/api/gateway-remote/${encodeURIComponent(id)}/app/`;
  }

  async adminGetGatewayReleases(): Promise<{ releases: GatewayRelease[]; versions: GatewayFleetVersion[] }> {
    const response = await this.client.get('/api/gateway-software/releases');
    return response.data.data;
  }

  async adminUpdateGatewayRelease(id: string, change: Partial<Pick<GatewayRelease, 'channel' | 'rollout_percent' | 'paused' | 'notes'>>): Promise<GatewayRelease> {
    const response = await this.client.patch(`/api/gateway-software/releases/${encodeURIComponent(id)}`, change);
    return response.data.data;
  }

  async adminSetGatewayChannel(serial: string, channel: 'test' | 'early' | 'stable'): Promise<{ serial: string; channel: string }> {
    const response = await this.client.put(`/api/gateway-software/devices/${encodeURIComponent(serial)}/channel`, { channel });
    return response.data.data;
  }

  async lookupDeviceByQR(serialNumber: string, modelCode?: string): Promise<QRCodeLookupResponse> {
    const response = await this.client.post('/api/devices/lookup', {
      serial_number: serialNumber,
      model_code: modelCode,
    });
    return response.data;
  }

  async claimDevice(input: ClaimDeviceInput): Promise<Device> {
    const response = await this.client.post('/api/devices/claim', input);
    return response.data;
  }

  async updateDevice(id: string, data: UpdateDeviceInput): Promise<Device> {
    const response = await this.client.put(`/api/devices/${id}`, data);
    return response.data;
  }

  async deleteDevice(id: string): Promise<{ success: boolean; message: string }> {
    const response = await this.client.delete(`/api/devices/${id}`);
    return response.data;
  }

  async getDeviceEntitlements(deviceId: string): Promise<DeviceEntitlement[]> {
    const response = await this.client.get(`/api/devices/${deviceId}/entitlements`);
    return response.data.entitlements;
  }

  // ==========================================
  // Admin Device Endpoints
  // ==========================================

  async adminGetDevices(params?: {
    limit?: number;
    page?: number;
    status?: string;
    search?: string;
    owner?: string;
    user_id?: string;
  }): Promise<{ devices: Device[]; total: number }> {
    const response = await this.client.get('/api/devices/admin/list', { params });
    return { devices: response.data.devices, total: response.data.pagination?.total ?? 0 };
  }

  async adminGetDeviceStats(): Promise<DeviceStats> {
    const response = await this.client.get('/api/devices/admin/stats');
    return response.data.stats;
  }

  async adminCreateDevice(data: CreateDeviceInput): Promise<Device> {
    const response = await this.client.post('/api/devices/inventory/create', data);
    return response.data;
  }

  async adminUpdateDeviceStatus(id: string, status: string): Promise<Device> {
    const response = await this.client.put(`/api/devices/admin/${id}/status`, { status });
    return response.data;
  }

  /** Owner-scoped unlink: the API allows the device's owner (or an admin). */
  async unlinkDevice(id: string): Promise<Device> {
    const response = await this.client.post(`/api/devices/${id}/unlink`);
    return response.data.device;
  }

  async adminUnlinkDevice(id: string): Promise<Device> {
    const response = await this.client.post(`/api/devices/admin/${id}/unlink`);
    return response.data.device;
  }

  async adminGetInventoryStats(): Promise<InventoryStats> {
    const response = await this.client.get('/api/devices/inventory/stats');
    return response.data.stats;
  }

  async adminReceiveDevice(serialNumber: string, location?: string, notes?: string): Promise<Device> {
    const response = await this.client.post('/api/devices/inventory/receive', {
      serial_number: serialNumber,
      location,
      notes,
    });
    return response.data;
  }

  async adminShipDevice(data: {
    serial_number: string;
    order_id: string;
    purchaser_id: string;
    notes?: string;
  }): Promise<Device> {
    const response = await this.client.post('/api/devices/inventory/ship', data);
    return response.data;
  }

  /** The Scout's safety-warning acceptances, newest first. */
  async adminGetDeviceSafetyAcceptances(deviceId: string): Promise<{ device_id: string; count: number; acceptances: SafetyAcceptance[] }> {
    const response = await this.client.get(`/api/devices/${deviceId}/safety-acceptances`);
    return response.data;
  }

  async adminGetDeviceAuditLog(deviceId: string): Promise<{ logs: DeviceAuditLog[]; total: number }> {
    const response = await this.client.get(`/api/devices/${deviceId}/audit-log`);
    return response.data;
  }

  // Device Detail - Get single device with full info
  async adminGetDevice(deviceId: string): Promise<Device> {
    const response = await this.client.get(`/api/devices/${deviceId}`);
    return response.data.device;
  }

  // TODO: GET /api/devices/:id/telemetry is not yet implemented in vortex-api.
  // Pending Tony's changes — the sync write-side exists (POST /api/sync/heartbeat) but the admin read-side does not.
  async adminGetDeviceTelemetry(deviceId: string): Promise<any | null> {
    try {
      const response = await this.client.get(`/api/devices/${deviceId}/telemetry`, {
        suppressErrorToast: [404],
      });
      return response.data;
    } catch (error) {
      // 404 means the device has never reported telemetry. The page's empty
      // sections communicate that; no error toast needed.
      if (isAxiosError(error) && error.response?.status === 404) return null;
      throw error;
    }
  }

  // TODO: GET /api/devices/:id/telemetry/history is not yet implemented in vortex-api. Pending Tony's changes.
  async adminGetDeviceTelemetryHistory(deviceId: string, hours: number = 24): Promise<any[]> {
    const response = await this.client.get(`/api/devices/${deviceId}/telemetry/history`, { params: { hours } });
    return response.data.history || [];
  }

  // TODO: GET /api/devices/admin/:id/ais/vessels is not yet implemented in vortex-api. Pending Tony's changes.
  async adminGetDeviceAisVessels(deviceId: string): Promise<any[]> {
    const response = await this.client.get(`/api/devices/admin/${deviceId}/ais/vessels`);
    return response.data.vessels || [];
  }

  // TODO: GET /api/devices/admin/:id/ais/sightings is not yet implemented in vortex-api. Pending Tony's changes.
  async adminGetDeviceAisSightings(deviceId: string, hours: number = 24): Promise<any[]> {
    const response = await this.client.get(`/api/devices/admin/${deviceId}/ais/sightings`, { params: { hours } });
    return response.data.sightings || [];
  }

  // TODO: GET /api/devices/:id/location-history is not yet implemented in vortex-api. Pending Tony's changes.
  async adminGetDeviceLocationHistory(deviceId: string, days: number = 7): Promise<any> {
    const response = await this.client.get(`/api/devices/${deviceId}/location-history`, { params: { days } });
    return response.data;
  }

  // Device Commands - List
  async adminGetDeviceCommands(deviceId: string, status?: string): Promise<{ commands: any[]; total: number }> {
    const params: any = { limit: 50 };
    if (status) params.status = status;
    const response = await this.client.get(`/api/devices/${deviceId}/commands`, { params });
    return response.data;
  }

  // Device Commands - Create
  async adminCreateDeviceCommand(deviceId: string, command: string, params?: Record<string, unknown>): Promise<any> {
    // The API's field names are `type`/`payload`. This sent `command`/`params`
    // for months, so every command from the portal was rejected with a 400.
    const response = await this.client.post(`/api/devices/${deviceId}/commands`, { type: command, payload: params ?? {} });
    return response.data;
  }

  // Devices needing attention — the signals that say something is WRONG,
  // as opposed to the status tiles which only say what a device is.
  async adminGetDeviceAttention(): Promise<any> {
    const response = await this.client.get('/api/devices/admin/attention');
    return response.data;
  }

  async adminGetDevicesNeedingAttention(reason?: string): Promise<any> {
    const response = await this.client.get('/api/devices/admin/attention/devices', {
      params: reason ? { reason } : undefined,
    });
    return response.data;
  }

  // Device Diagnostics — log/crash bundles pulled off a Scout
  async adminListDeviceDiagnostics(deviceId: string): Promise<any> {
    const response = await this.client.get(`/api/devices/${deviceId}/diagnostics`);
    return response.data;
  }

  /** With a symptom, the API diagnoses the bundle automatically when it arrives. */
  async adminRequestDeviceDiagnostics(deviceId: string, symptom?: string): Promise<any> {
    const response = await this.client.post(`/api/devices/${deviceId}/diagnostics/collect`, symptom ? { symptom } : {});
    return response.data;
  }

  // AI Diagnose — Claude reads a bundle and writes a report. 400/409/429/503
  // are expected answers (bad symptom, already running, daily cap, not
  // configured) that the Diagnostics tab explains in place, so no toast.
  async adminDiagnoseBundle(deviceId: string, bundleId: string, symptom?: string): Promise<DiagnosticReport> {
    const response = await this.client.post(
      `/api/devices/${deviceId}/diagnostics/${bundleId}/diagnose`,
      symptom ? { symptom } : {},
      { suppressErrorToast: [400, 409, 429, 503] },
    );
    return response.data.data;
  }

  async adminGetDiagnosticReport(deviceId: string, reportId: string): Promise<DiagnosticReport> {
    const response = await this.client.get(`/api/devices/${deviceId}/diagnostic-reports/${reportId}`);
    return response.data.data;
  }

  /** Newest first. */
  async adminListDiagnosticReports(deviceId: string, bundleId?: string): Promise<DiagnosticReport[]> {
    const response = await this.client.get(`/api/devices/${deviceId}/diagnostic-reports`, {
      params: bundleId ? { bundle_id: bundleId } : undefined,
    });
    return response.data.data ?? [];
  }

  /** A signed, login-free fix brief for a Claude Code session; expires in 7 days. */
  async adminShareDiagnosticReport(deviceId: string, reportId: string): Promise<DiagnosticShareLink> {
    const response = await this.client.post(`/api/devices/${deviceId}/diagnostic-reports/${reportId}/share`, {});
    return response.data.data;
  }

  /** Direct link — the bundle streams from R2, so it is not fetched through axios. */
  deviceDiagnosticsDownloadUrl(deviceId: string, bundleId: string): string {
    return `${API_BASE_URL}/api/devices/${deviceId}/diagnostics/${bundleId}`;
  }

  // Device Commands - Cancel
  async adminCancelDeviceCommand(deviceId: string, commandId: string): Promise<any> {
    const response = await this.client.delete(`/api/devices/${deviceId}/commands/${commandId}`);
    return response.data;
  }

  // Device Config
  async adminGetDeviceConfig(deviceId: string): Promise<any> {
    const response = await this.client.get(`/api/devices/${deviceId}/config`);
    return response.data;
  }

  async adminUpdateDeviceConfig(deviceId: string, config: Record<string, unknown>): Promise<any> {
    const response = await this.client.put(`/api/devices/${deviceId}/config`, config);
    return response.data;
  }

  // ==========================================
  // Admin Organization / Fleet Endpoints
  // ==========================================

  async adminListOrganizations(params?: {
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ organizations: any[]; total: number }> {
    const response = await this.client.get('/api/organizations', { params });
    return {
      organizations: response.data.data || [],
      total: response.data.pagination?.total ?? (response.data.data?.length || 0),
    };
  }

  async adminCreateOrganization(data: {
    name: string;
    slug: string;
    contact_email?: string;
    notes?: string;
  }): Promise<any> {
    const response = await this.client.post('/api/organizations', data);
    return response.data.data || response.data;
  }

  async adminGetOrganization(id: string): Promise<any> {
    const response = await this.client.get(`/api/organizations/${id}`);
    return response.data.data || response.data;
  }

  async adminUpdateOrganization(id: string, data: any): Promise<any> {
    const response = await this.client.put(`/api/organizations/${id}`, data);
    return response.data.data || response.data;
  }

  async adminDeleteOrganization(id: string): Promise<void> {
    await this.client.delete(`/api/organizations/${id}`);
  }

  // TODO: Implement GET /api/organizations/:id/devices (or /api/fleet/:id/devices) when merging Tony's changes.
  // The fleet endpoint at /api/fleet/:orgId/devices may cover this — wire up after reviewing his branch.
  async adminGetOrganizationDevices(orgId: string): Promise<any[]> {
    const response = await this.client.get(`/api/fleet/${orgId}/devices`);
    return response.data.data || response.data.devices || [];
  }

  async adminGetFleetHealth(orgId: string): Promise<any> {
    const response = await this.client.get(`/api/organizations/${orgId}/fleet`);
    return response.data.data || response.data;
  }

  async adminGetFleetMapMarkers(): Promise<FleetMapMarker[]> {
    const response = await this.client.get('/api/fleet/map');
    return response.data.markers ?? [];
  }

  async adminCreateDeviceGroup(orgId: string, data: {
    name: string;
    description?: string;
    color?: string;
  }): Promise<any> {
    const response = await this.client.post(`/api/organizations/${orgId}/groups`, data);
    return response.data.data || response.data;
  }

  async adminListDeviceGroups(orgId: string): Promise<any[]> {
    const response = await this.client.get(`/api/organizations/${orgId}/groups`);
    return response.data.data || [];
  }

  // TODO: Implement POST /api/organizations/:id/commands (bulk command dispatch) when merging Tony's changes.
  // Requires org-level command fanout logic not yet in vortex-api.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async adminBulkCommand(_orgId: string, _command: string, _params?: any): Promise<any> {
    console.warn('[API] adminBulkCommand not yet implemented in vortex-api — pending Tony\'s changes');
    return { success: false, error: 'Not yet implemented' };
  }

  // ==========================================
  // Admin Esri Location Services
  // ==========================================

  async adminGetEsriDashboard(): Promise<any> {
    const response = await this.client.get('/api/admin/esri/dashboard');
    return response.data.data;
  }

  async adminGetEsriConfig(): Promise<any> {
    const response = await this.client.get('/api/admin/esri/config');
    return response.data.data;
  }

  async adminUpdateEsriConfig(config: Record<string, unknown>): Promise<any> {
    const response = await this.client.put('/api/admin/esri/config', config);
    return response.data.data;
  }

  async adminGetEsriDeviceUsage(): Promise<any[]> {
    const response = await this.client.get('/api/admin/esri/devices');
    return response.data.data;
  }

  async adminGetUserEsriUsage(userId: string): Promise<any> {
    const response = await this.client.get(`/api/admin/esri/users/${userId}`);
    return response.data.data;
  }

  async adminGetEsriTrend(days = 30): Promise<{ device: { date: string; tiles: number }[]; esri: { date: string; tiles: number }[] }> {
    const response = await this.client.get(`/api/admin/esri/trend?days=${days}`);
    return response.data.data;
  }

  async adminEsriSyncNow(): Promise<{ ok: boolean; days_upserted: number; services: string[]; error?: string }> {
    const response = await this.client.post('/api/admin/esri/sync-now');
    return response.data.data;
  }

  // ==========================================
  // MyPolaris Navigation Library
  // ==========================================

  async navAccess(): Promise<{ enabled: boolean }> {
    const response = await this.client.get('/api/nav/access');
    return response.data.data;
  }

  async navList(scope: 'mine' | 'shared'): Promise<import('../types/nav').NavElement[]> {
    const response = await this.client.get(`/api/nav?scope=${scope}`);
    return response.data.data;
  }

  async navUpload(gpx: string, fileName?: string): Promise<import('../types/nav').NavElement[]> {
    const response = await this.client.post('/api/nav/upload', { gpx, file_name: fileName });
    return response.data.data;
  }

  /**
   * Build a GPX for the selected elements. 'original' serves the archived
   * full-resolution recording for anything thinned at ingest; `degraded` counts
   * elements whose archive could not be read and fell back to stored geometry.
   */
  async navDownload(
    ids: string[],
    fidelity: 'simplified' | 'original' = 'simplified',
  ): Promise<{ gpx: string; count: number; fidelity: 'simplified' | 'original'; degraded: number }> {
    const response = await this.client.post('/api/nav/download', { ids, fidelity });
    return response.data.data;
  }

  // ---- The boat (vessel dimensions; not feature-gated) ----

  async vesselList(): Promise<import('../types/nav').VesselProfile[]> {
    const response = await this.client.get('/api/vessels');
    return response.data.data;
  }

  async vesselCreate(name: string): Promise<import('../types/nav').VesselProfile> {
    const response = await this.client.post('/api/vessels', { name });
    return response.data.data;
  }

  /** Dimensions in METRES. Rejected server-side if implausible. */
  async vesselSetDimensions(id: string, dims: {
    vessel_type?: string | null;
    propulsion?: string | null;
    loa_m?: number | null;
    beam_m?: number | null;
    draft_m?: number | null;
    air_draft_m?: number | null;
  }): Promise<import('../types/nav').VesselProfile> {
    const response = await this.client.patch(`/api/vessels/${id}/dimensions`, dims, { suppressErrorToast: [400] });
    return response.data.data;
  }

  /** Which of my boats made this — re-stamps the dimension snapshot. */
  async navSetVessel(id: string, vesselId: string | null): Promise<import('../types/nav').NavElement> {
    const response = await this.client.put(`/api/nav/${id}/vessel`, { vessel_id: vesselId });
    return response.data.data;
  }

  // ---- Discovery: the map is the query ----

  /** What is in this viewport. `bbox` is west,south,east,north. */
  async navDiscover(params: { bbox?: string; q?: string; types?: string }): Promise<{
    items: import('../types/nav').NavElement[]; total: number; truncated: boolean;
  }> {
    const response = await this.client.get('/api/nav/discover', { params });
    return response.data.data;
  }

  /** Which passages cross this line. `line` is lat1,lon1,lat2,lon2. */
  async navCrossing(line: string): Promise<{
    items: Array<{ element: import('../types/nav').NavElement; at: [number, number] }>; total: number;
  }> {
    const response = await this.client.get('/api/nav/crossing', {
      params: { line }, suppressErrorToast: [400],
    });
    return response.data.data;
  }

  /** Resolve a place NAME to a place. */
  async navPlaces(q: string, limit = 10): Promise<import('../types/nav').PlaceHit[]> {
    const response = await this.client.get('/api/nav/places', { params: { q, limit } });
    return response.data.data;
  }

  /** Where the corpus is dense, and the box enclosing all of it. */
  async navDensity(cell = 5): Promise<{
    clusters: Array<{ lat: number; lon: number; count: number }>;
    coverage: { west: number; south: number; east: number; north: number } | null;
  }> {
    const response = await this.client.get('/api/nav/density', { params: { cell } });
    return response.data.data;
  }

  /** What each Scout on the account holds of this element. */
  async navBoats(id: string): Promise<import('../types/nav').BoatState[]> {
    const response = await this.client.get(`/api/nav/${id}/boats`);
    return response.data.data;
  }

  async navUpdate(id: string, patch: { name?: string; tags?: string[]; description?: string | null }): Promise<void> {
    await this.client.patch(`/api/nav/${id}`, patch);
  }

  /** Save — overwrite an element's geometry in place. Owner-authored items only. */
  async navSaveGeometry(
    id: string,
    points: [number, number][],
    waypointNames: string[] | null,
  ): Promise<import('../types/nav').NavElement> {
    const response = await this.client.put(`/api/nav/${id}/geometry`, { points, waypoint_names: waypointNames });
    return response.data.data;
  }

  /** Save As, a drawn route/waypoint, or a converted track. `sourceId` records the parent. */
  async navDraw(input: {
    type: 'route' | 'waypoint';
    name: string;
    points: [number, number][];
    waypointNames?: string[] | null;
    sourceId?: string | null;
  }): Promise<import('../types/nav').NavElement> {
    const response = await this.client.post('/api/nav/draw', {
      type: input.type,
      name: input.name,
      points: input.points,
      waypoint_names: input.waypointNames ?? null,
      source_id: input.sourceId ?? null,
    });
    return response.data.data;
  }

  async navDelete(id: string): Promise<void> {
    await this.client.delete(`/api/nav/${id}`);
  }

  async navShare(id: string, email: string): Promise<{ to: string }> {
    const response = await this.client.post(`/api/nav/${id}/share`, { email });
    return response.data.data;
  }

  async navSaveCopy(id: string): Promise<void> {
    await this.client.post(`/api/nav/${id}/save`);
  }

  /** Queue a library element for silent delivery to one of the user's Scouts. */
  async navSendToBoat(id: string, deviceId: string): Promise<{ command_id: string; device_name: string | null; online: boolean }> {
    const response = await this.client.post(`/api/nav/${id}/send-to-boat`, { device_id: deviceId });
    return response.data.data;
  }

  // ---- Shared Navigation Libraries (same nav_library gate) ----
  async navLibExplore(q?: string, sort: import('../types/navLibraries').ExploreSort = 'top'): Promise<import('../types/navLibraries').ExploreLibrary[]> {
    const response = await this.client.get('/api/nav-libraries/explore', { params: { ...(q ? { q } : {}), sort } });
    return response.data.data;
  }

  async navLibCurator(userId: string): Promise<import('../types/navLibraries').CuratorProfile> {
    const response = await this.client.get(`/api/nav-libraries/curators/${userId}`);
    return response.data.data;
  }

  async navLibRate(id: string, input: { stars: number; tags: string[]; text?: string | null }): Promise<import('../types/navLibraries').LibraryRating> {
    const response = await this.client.put(`/api/nav-libraries/${id}/ratings`, input, { suppressErrorToast: [400, 403] });
    return response.data.data;
  }

  async navLibUnrate(id: string): Promise<void> {
    await this.client.delete(`/api/nav-libraries/${id}/ratings`);
  }

  async navLibReport(id: string, input: { reason: import('../types/navLibraries').ReportReason; details?: string | null }): Promise<{ id: string }> {
    const response = await this.client.post(`/api/nav-libraries/${id}/report`, input, { suppressErrorToast: [400, 409] });
    return response.data.data;
  }

  // ---- admin: PassageWiki moderation (reports on public passages) ----
  async adminPassageReports(status?: import('../types/passageReports').PassageReportStatus): Promise<import('../types/passageReports').PassageReport[]> {
    const response = await this.client.get('/api/admin/passage-reports', { params: status ? { status } : undefined });
    return response.data.data;
  }

  async adminPassageOpenReportCount(): Promise<{ open: number }> {
    const response = await this.client.get('/api/admin/passage-reports/count');
    return response.data.data;
  }

  /** `unpublish` withdraws the passage from passagewiki.com as part of settling; nothing is deleted. */
  async adminPassageResolveReport(id: string, input: { status: 'resolved' | 'dismissed'; note?: string | null; unpublish?: boolean }): Promise<void> {
    await this.client.post(`/api/admin/passage-reports/${id}`, input);
  }

  // ---- admin: shared library moderation ----
  async adminNavLibReports(status?: import('../types/navLibraries').ReportStatus): Promise<import('../types/navLibraries').LibraryReport[]> {
    const response = await this.client.get('/api/admin/nav-libraries/reports', { params: status ? { status } : undefined });
    return response.data.data;
  }

  async adminNavLibOpenReportCount(): Promise<{ open: number }> {
    const response = await this.client.get('/api/admin/nav-libraries/reports/count');
    return response.data.data;
  }

  async adminNavLibResolveReport(id: string, input: { status: 'resolved' | 'dismissed'; note?: string | null; unlist?: boolean }): Promise<void> {
    await this.client.post(`/api/admin/nav-libraries/reports/${id}`, input);
  }

  /** Undo a takedown: the library is back as its curator had it. */
  async adminNavLibRestore(libraryId: string): Promise<void> {
    await this.client.post(`/api/admin/nav-libraries/${libraryId}/restore`, {});
  }

  // ==========================================
  // Ship's Papers (vessel document library; gated per vessel on the owner's packages.document_library)
  // ==========================================
  async papersAccess(): Promise<import('../types/papers').PapersAccess> {
    const response = await this.client.get('/api/papers/access');
    return response.data.data;
  }
  async papersCatalog(): Promise<import('../types/papers').Catalog> {
    const response = await this.client.get('/api/papers/catalog');
    return response.data.data;
  }
  async papersVessel(vid: string): Promise<import('../types/papers').VesselDetail> {
    const response = await this.client.get(`/api/papers/vessels/${vid}`, { suppressErrorToast: [403, 404] }); // the page says it
    return response.data.data;
  }
  async papersUpdateVessel(vid: string, patch: { name?: string; mmsi?: string | null; call_sign?: string | null; flag?: string | null; home_port?: string | null }): Promise<void> {
    await this.client.patch(`/api/papers/vessels/${vid}`, patch, { suppressErrorToast: [400, 403] });
  }
  async papersChecklist(vid: string, section: string, type: string, notApplicable: boolean): Promise<void> {
    await this.client.post(`/api/papers/vessels/${vid}/checklist`, { section, type, not_applicable: notApplicable });
  }
  async papersReadiness(vid: string): Promise<import('../types/papers').Readiness> {
    const response = await this.client.get(`/api/papers/vessels/${vid}/readiness`);
    return response.data.data;
  }
  async papersInvite(vid: string, input: { email: string; role: string; title?: string | null; expires_at?: string | null }): Promise<{ memberId: string; alreadyAccount: boolean }> {
    const response = await this.client.post(`/api/papers/vessels/${vid}/invites`, input, { suppressErrorToast: [400, 403, 409] });
    return response.data.data;
  }
  async papersInviteInfo(token: string): Promise<import('../types/papers').InviteInfo> {
    const response = await this.client.get(`/api/papers/invites/${token}`, { suppressErrorToast: [404] });
    return response.data.data;
  }
  async papersAcceptInvite(token: string): Promise<{ vesselId: string; role: string }> {
    const response = await this.client.post(`/api/papers/invites/${token}/accept`, {}, { suppressErrorToast: [403, 404, 409] });
    return response.data.data;
  }
  async papersUpdateMember(vid: string, mid: string, patch: { role?: string; title?: string | null; expires_at?: string | null }): Promise<void> {
    await this.client.patch(`/api/papers/vessels/${vid}/members/${mid}`, patch, { suppressErrorToast: [400, 403] });
  }
  async papersRemoveMember(vid: string, mid: string): Promise<void> {
    await this.client.delete(`/api/papers/vessels/${vid}/members/${mid}`, { suppressErrorToast: [400, 403] });
  }
  async papersDocuments(vid: string, params: { section?: string; folder?: string; q?: string; equipment_item_id?: string }): Promise<import('../types/papers').PaperDoc[]> {
    const response = await this.client.get(`/api/papers/vessels/${vid}/documents`, { params });
    return response.data.data;
  }
  async papersCreateDocument(vid: string, input: import('../types/papers').DocumentInput): Promise<import('../types/papers').PaperDoc> {
    const response = await this.client.post(`/api/papers/vessels/${vid}/documents`, input, { suppressErrorToast: [400, 403, 404] });
    return response.data.data;
  }
  async papersGetDocument(vid: string, id: string): Promise<import('../types/papers').PaperDoc> {
    const response = await this.client.get(`/api/papers/vessels/${vid}/documents/${id}`);
    return response.data.data;
  }
  async papersUpdateDocument(vid: string, id: string, patch: Partial<import('../types/papers').DocumentInput>): Promise<import('../types/papers').PaperDoc> {
    const response = await this.client.patch(`/api/papers/vessels/${vid}/documents/${id}`, patch, { suppressErrorToast: [400, 403, 404] });
    return response.data.data;
  }
  async papersDeleteDocument(vid: string, id: string): Promise<void> {
    await this.client.delete(`/api/papers/vessels/${vid}/documents/${id}`, { suppressErrorToast: [403] });
  }
  async papersUploadFile(vid: string, id: string, file: File, text?: string | null): Promise<import('../types/papers').DocFile> {
    const fd = new FormData(); fd.append('file', file); if (text) fd.append('text', text);
    // Long timeout: a 15 MB manual over marina Wi-Fi outlives the 30 s JSON default.
    const response = await this.client.post(`/api/papers/vessels/${vid}/documents/${id}/files`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, suppressErrorToast: [400, 403, 413, 415, 503], timeout: BLOB_TIMEOUT });
    return response.data.data;
  }
  /** Fetches the bytes with the session cookie so they can be previewed or saved. */
  async papersFileBlob(vid: string, id: string, fid: string, download = false): Promise<Blob> {
    const response = await this.client.get(`/api/papers/vessels/${vid}/documents/${id}/files/${fid}`, { params: download ? { download: 1 } : undefined, responseType: 'blob', timeout: BLOB_TIMEOUT });
    return response.data as Blob;
  }
  async papersEquipment(vid: string): Promise<import('../types/papers').EquipmentItem[]> {
    const response = await this.client.get(`/api/papers/vessels/${vid}/equipment`);
    return response.data.data;
  }
  async papersCreateEquipment(vid: string, input: import('../types/papers').EquipmentInput): Promise<import('../types/papers').EquipmentItem> {
    const response = await this.client.post(`/api/papers/vessels/${vid}/equipment`, input, { suppressErrorToast: [400, 403] });
    return response.data.data;
  }
  async papersUpdateEquipment(vid: string, eid: string, patch: Partial<import('../types/papers').EquipmentInput>): Promise<import('../types/papers').EquipmentItem> {
    const response = await this.client.patch(`/api/papers/vessels/${vid}/equipment/${eid}`, patch, { suppressErrorToast: [400, 403, 404] });
    return response.data.data;
  }
  async papersDeleteEquipment(vid: string, eid: string): Promise<void> {
    await this.client.delete(`/api/papers/vessels/${vid}/equipment/${eid}`, { suppressErrorToast: [403] });
  }
  async papersEvents(vid: string): Promise<import('../types/papers').PapersEvent[]> {
    const response = await this.client.get(`/api/papers/vessels/${vid}/events`);
    return response.data.data;
  }

  // ---- Ship's Papers — shared document libraries ----
  async paperLibExplore(p: { q?: string; sort?: import('../types/navLibraries').ExploreSort; section?: string }): Promise<import('../types/papers').PaperLibrary[]> {
    const response = await this.client.get('/api/paper-libraries/explore', { params: p });
    return response.data.data;
  }
  async paperLibMine(): Promise<import('../types/papers').PaperLibrary[]> { const r = await this.client.get('/api/paper-libraries/mine'); return r.data.data; }
  async paperLibSubscribed(): Promise<import('../types/papers').PaperLibrary[]> { const r = await this.client.get('/api/paper-libraries/subscribed'); return r.data.data; }
  async paperLibCreate(input: import('../types/papers').PaperLibraryInput): Promise<import('../types/papers').PaperLibrary> { const r = await this.client.post('/api/paper-libraries', input, { suppressErrorToast: [400] }); return r.data.data; }
  async paperLibUpdate(id: string, patch: Partial<import('../types/papers').PaperLibraryInput>): Promise<import('../types/papers').PaperLibrary> { const { section: _s, ...rest } = patch; void _s; const r = await this.client.patch(`/api/paper-libraries/${id}`, rest, { suppressErrorToast: [400] }); return r.data.data; }
  async paperLibDelete(id: string): Promise<void> { await this.client.delete(`/api/paper-libraries/${id}`, { suppressErrorToast: [400, 403, 404] }); }
  async paperLibDetail(id: string, vesselId?: string, page?: { q?: string; limit?: number; offset?: number }): Promise<import('../types/papers').PaperLibraryDetail> { const r = await this.client.get(`/api/paper-libraries/${id}`, { suppressErrorToast: [403, 404], params: { ...(vesselId ? { vessel: vesselId } : {}), ...(page?.q ? { q: page.q } : {}), ...(page?.limit != null ? { limit: page.limit } : {}), ...(page?.offset ? { offset: page.offset } : {}) } }); return r.data.data; }
  async papersLookupBySha(vid: string, sha256s: string[]): Promise<Array<{ sha256: string; documentId: string; title: string }>> { if (!sha256s.length) return []; const r = await this.client.post(`/api/papers/vessels/${vid}/documents/lookup`, { sha256s }); return r.data.data; }
  /** Bulk delete, chunked at the API's 100-per-request cap; aggregates per-item results. */
  async papersBulkDeleteDocuments(vid: string, documentIds: string[]): Promise<{ deleted: number; failed: number; results: Array<{ id: string; ok: boolean; error?: string }> }> {
    const out = { deleted: 0, failed: 0, results: [] as Array<{ id: string; ok: boolean; error?: string }> };
    for (let i = 0; i < documentIds.length; i += 100) {
      const r = await this.client.post(`/api/papers/vessels/${vid}/documents/bulk-delete`, { document_ids: documentIds.slice(i, i + 100) });
      const d = r.data.data as typeof out;
      out.deleted += d.deleted; out.failed += d.failed; out.results.push(...d.results);
    }
    return out;
  }
  async paperLibAddItems(id: string, documentIds: string[]): Promise<{ added: number; skipped: number; copiesSkipped?: string[]; originalsUsed?: number; copiesAdopted?: number }> { const r = await this.client.post(`/api/paper-libraries/${id}/items`, { document_ids: documentIds }, { suppressErrorToast: [400, 403] }); return r.data.data; }
  async paperLibRemoveItem(id: string, documentId: string): Promise<void> { await this.client.delete(`/api/paper-libraries/${id}/items/${documentId}`, { suppressErrorToast: [400, 403, 404] }); }
  async paperLibMembers(id: string): Promise<import('../types/papers').PaperLibraryMember[]> { const r = await this.client.get(`/api/paper-libraries/${id}/members`); return r.data.data; }
  async paperLibDecide(id: string, userId: string, status: 'approved' | 'declined' | 'revoked'): Promise<void> { await this.client.post(`/api/paper-libraries/${id}/members/${userId}`, { status }, { suppressErrorToast: [400, 403, 404] }); }
  async paperLibRequest(id: string, message?: string): Promise<{ status: string }> { const r = await this.client.post(`/api/paper-libraries/${id}/request`, { message: message ?? null }, { suppressErrorToast: [400, 409] }); return r.data.data; }
  async paperLibLeave(id: string): Promise<void> { await this.client.post(`/api/paper-libraries/${id}/leave`, {}, { suppressErrorToast: [400, 403, 404] }); }
  async paperLibCopy(id: string, documentIds: string[], vesselId: string): Promise<{ copied: number; ids: string[] }> { const r = await this.client.post(`/api/paper-libraries/${id}/copy`, { document_ids: documentIds, vessel_id: vesselId }, { suppressErrorToast: [400, 403, 404] }); return r.data.data; }
  async paperLibFileBlob(id: string, documentId: string, download = false): Promise<Blob> { const r = await this.client.get(`/api/paper-libraries/${id}/documents/${documentId}/file`, { params: download ? { download: 1 } : undefined, responseType: 'blob', timeout: BLOB_TIMEOUT }); return r.data as Blob; }
  async paperLibRate(id: string, input: { stars: number; tags: string[]; text?: string | null }): Promise<import('../types/navLibraries').LibraryRating> { const r = await this.client.put(`/api/paper-libraries/${id}/ratings`, input, { suppressErrorToast: [400, 403] }); return r.data.data; }
  async paperLibUnrate(id: string): Promise<void> { await this.client.delete(`/api/paper-libraries/${id}/ratings`, { suppressErrorToast: [400, 403, 404] }); }
  async paperLibReport(id: string, input: { reason: import('../types/navLibraries').ReportReason; details?: string | null }): Promise<{ id: string }> { const r = await this.client.post(`/api/paper-libraries/${id}/report`, input, { suppressErrorToast: [400, 409] }); return r.data.data; }
  async paperLibPublishedIn(documentIds: string[]): Promise<Array<{ documentId: string; libraryId: string; name: string }>> { if (!documentIds.length) return []; const r = await this.client.post('/api/paper-libraries/published-in', { document_ids: documentIds }); return r.data.data; }
  async adminPaperLibReports(status?: import('../types/navLibraries').ReportStatus): Promise<import('../types/navLibraries').LibraryReport[]> { const r = await this.client.get('/api/admin/paper-libraries/reports', { params: status ? { status } : undefined }); return r.data.data; }
  async adminPaperLibResolveReport(id: string, input: { status: 'resolved' | 'dismissed'; note?: string | null; unlist?: boolean }): Promise<void> { await this.client.post(`/api/admin/paper-libraries/reports/${id}`, input); }
  async adminPaperLibRestore(libraryId: string): Promise<void> { await this.client.post(`/api/admin/paper-libraries/${libraryId}/restore`, {}); }

  // ---- Ship's Papers — share links, entry packs, public ----
  async papersShares(vid: string, id: string): Promise<import('../types/papers').DocShare[]> { const r = await this.client.get(`/api/papers/vessels/${vid}/documents/${id}/shares`); return r.data.data; }
  async papersCreateShare(vid: string, id: string, input: { days?: number; label?: string | null }): Promise<{ share: import('../types/papers').DocShare; url: string }> { const r = await this.client.post(`/api/papers/vessels/${vid}/documents/${id}/shares`, input, { suppressErrorToast: [400, 403] }); return r.data.data; }
  async papersRevokeShare(vid: string, id: string, sid: string): Promise<void> { await this.client.delete(`/api/papers/vessels/${vid}/documents/${id}/shares/${sid}`, { suppressErrorToast: [403, 404] }); }
  /** The link's creator moves it to the document's current version (API from 2026-09-26). */
  async papersShareToLatest(vid: string, id: string, sid: string): Promise<import('../types/papers').DocShare> { const r = await this.client.post(`/api/papers/vessels/${vid}/documents/${id}/shares/${sid}/latest`, {}, { suppressErrorToast: [400, 403, 404] }); return r.data.data; }
  async papersPackSuggested(vid: string): Promise<import('../types/papers').PackSuggestion> { const r = await this.client.get(`/api/papers/vessels/${vid}/packs/suggested`); return r.data.data; }
  /** `included` is null when CORS hides the pack headers; errors carry a JSON blob (utils/entryPack blobErrorMessage) and are said by the modal. */
  async papersBuildPack(vid: string, input: { title: string; document_ids: string[]; include_crew_list?: boolean; include_vessel_page?: boolean }): Promise<{ blob: Blob; fileName: string; included: number | null; skipped: string[] }> {
    const r = await this.client.post(`/api/papers/vessels/${vid}/packs`, input, { responseType: 'blob', suppressErrorToast: [400, 403, 404, 413, 503], timeout: BLOB_TIMEOUT });
    return { blob: r.data as Blob, ...parsePackHeaders(r.headers as Record<string, unknown>, input.title) };
  }
  async papersPublicInfo(token: string): Promise<import('../types/papers').PublicDocument> { const r = await this.client.get(`/api/papers-public/${token}`, { suppressErrorToast: [404] }); return r.data.data; }
  async papersMyPin(vid: string): Promise<{ has_pin: boolean }> { const r = await this.client.get(`/api/papers/vessels/${vid}/me/pin`); return r.data.data; }
  async papersSetMyPin(vid: string, pin: string): Promise<void> { await this.client.put(`/api/papers/vessels/${vid}/me/pin`, { pin }, { suppressErrorToast: [400, 409, 429] }); }
  async papersClearMyPin(vid: string): Promise<void> { await this.client.delete(`/api/papers/vessels/${vid}/me/pin`); }
    async papersPublicFile(token: string, download = false): Promise<Blob> { const r = await this.client.get(`/api/papers-public/${token}/file`, { params: download ? { download: 1 } : undefined, responseType: 'blob', timeout: BLOB_TIMEOUT, suppressErrorToast: [404, 413, 415] }); return r.data as Blob; }

  // ---- Ship's Papers — crew paper requests (docs/wip/CREW_PAPER_REQUESTS.md § API contract) ----
  // Every status the pages handle inline is kept out of the global toast (CREW_QUIET):
  // the crew flow shows each error where it happened, so it is never lost or doubled.
  // Link: public, no session. The token rides in the path; the API redacts it from logs.
  async crewLinkView(token: string): Promise<CP.PublicRequestView> { const r = await this.client.get(`/api/papers-requests-public/${encodeURIComponent(token)}`, { suppressErrorToast: CREW_QUIET }); return r.data.data; }
  async crewSendCode(token: string): Promise<{ maskedEmail: string; expiresInSeconds: number }> { const r = await this.client.post(`/api/papers-requests-public/${encodeURIComponent(token)}/send-code`, {}, { suppressErrorToast: CREW_QUIET }); return r.data.data; }
  /** Signs in (the response sets the session cookies) and claims the request. */
  async crewSignIn(token: string, code: string): Promise<CP.SignInResult> { const r = await this.client.post(`/api/papers-requests-public/${encodeURIComponent(token)}/sign-in`, { code }, { suppressErrorToast: CREW_QUIET }); return r.data.data; }
  async crewClaim(token: string): Promise<CP.ClaimResult> { const r = await this.client.post('/api/papers/requests/claim', { token }, { suppressErrorToast: CREW_QUIET }); return r.data.data; }
  // Ask to extend (Phase 2.3): the crew member's one-tap link — public view and code sign-in, then a
  // session call with the token in the body — and the skipper's ask.
  async extendView(token: string): Promise<CP.ExtensionView> { const r = await this.client.get(`/api/papers-requests-public/extend/${encodeURIComponent(token)}`, { suppressErrorToast: CREW_QUIET }); return r.data.data; }
  async extendSendCode(token: string): Promise<{ maskedEmail: string; expiresInSeconds: number }> { const r = await this.client.post(`/api/papers-requests-public/extend/${encodeURIComponent(token)}/send-code`, {}, { suppressErrorToast: CREW_QUIET }); return r.data.data; }
  /** Signs in as the recipient (the response sets the session cookies). */
  async extendSignIn(token: string, code: string): Promise<{ user: { id: string; name: string | null; email: string }; view: CP.ExtensionView }> { const r = await this.client.post(`/api/papers-requests-public/extend/${encodeURIComponent(token)}/sign-in`, { code }, { suppressErrorToast: CREW_QUIET }); return r.data.data; }
  async extendDecide(token: string, decision: 'approve' | 'decline'): Promise<CP.ExtensionView> { const r = await this.client.post('/api/papers/requests/extend/decide', { token, decision }, { suppressErrorToast: CREW_QUIET }); return r.data.data; }
  async skipperAskExtend(vid: string, rid: string, recId: string, untilOn: string): Promise<CP.ExtendAskResult> { const r = await this.client.post(`/api/papers/vessels/${vid}/requests/${rid}/recipients/${recId}/extend`, { untilOn }, { suppressErrorToast: CREW_QUIET }); return r.data.data; }
  // Me: my requests, papers and shares (papers-only accounts too).
  async papersMeAccess(): Promise<CP.MeAccess> { const r = await this.client.get('/api/papers/me/access', { suppressErrorToast: CREW_QUIET }); return r.data.data; }
  async papersMyRequests(): Promise<CP.MyRequestView[]> { const r = await this.client.get('/api/papers/me/requests', { suppressErrorToast: CREW_QUIET }); return r.data.data; }
  async papersMyPapers(): Promise<CP.MyPaper[]> { const r = await this.client.get('/api/papers/me/documents', { suppressErrorToast: CREW_QUIET }); return r.data.data; }
  async papersMyShares(): Promise<CP.MyShares> { const r = await this.client.get('/api/papers/me/shares', { suppressErrorToast: CREW_QUIET }); return r.data.data; }
  async papersDeleteMyPaper(id: string): Promise<void> { await this.client.delete(`/api/papers/me/documents/${id}`, { suppressErrorToast: CREW_QUIET }); }
  async papersMyFileBlob(id: string, fid: string, download = false): Promise<Blob> { const r = await this.client.get(`/api/papers/me/documents/${id}/files/${fid}`, { params: download ? { download: 1 } : undefined, responseType: 'blob', timeout: BLOB_TIMEOUT, suppressErrorToast: CREW_QUIET }); return r.data as Blob; }
  async papersMyDocument(id: string): Promise<CP.MyPaper & { files: Array<{ id: string; fileName: string; mime: string; sizeBytes: number }> }> { const r = await this.client.get(`/api/papers/me/documents/${id}`, { suppressErrorToast: CREW_QUIET }); return r.data.data; }
  /** My paper's encrypted fields (passport details, an emergency contact) — to prefill a replacement. */
  async papersMyDetails(id: string): Promise<Record<string, string | null> | null> { const r = await this.client.get(`/api/papers/me/documents/${id}/details`, { suppressErrorToast: CREW_QUIET }); return r.data.data.details; }
  // Crew on a request (claimed recipient).
  async crewRequest(rid: string): Promise<CP.CrewRequestView> { const r = await this.client.get(`/api/papers/requests/${rid}/me`, { suppressErrorToast: CREW_QUIET }); return r.data.data; }
  /** Multipart upload on the long timeout — a 30 s JSON timeout cut off photos on marina Wi-Fi. */
  async crewUpload(rid: string, iid: string, file: File, fields: CP.UploadFields, onProgress?: (fraction: number) => void): Promise<CP.CrewRequestView> {
    const fd = new FormData();
    fd.append('file', file);
    for (const k of ['title', 'issuer', 'reference_no', 'issued_on', 'expires_on'] as const) { const v = fields[k]; if (v) fd.append(k, v); }
    if (fields.details) fd.append('details', JSON.stringify(fields.details));
    const r = await this.client.post(`/api/papers/requests/${rid}/me/items/${iid}/upload`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' }, timeout: BLOB_TIMEOUT, suppressErrorToast: CREW_QUIET,
      onUploadProgress: (e) => { if (onProgress && e.total) onProgress(Math.min(1, e.loaded / e.total)); },
    });
    return r.data.data;
  }
  async crewReuse(rid: string, iid: string, documentId: string): Promise<CP.CrewRequestView> { const r = await this.client.post(`/api/papers/requests/${rid}/me/items/${iid}/reuse`, { documentId }, { suppressErrorToast: CREW_QUIET }); return r.data.data; }
  async crewForm(rid: string, iid: string, details: Record<string, string | null>): Promise<CP.CrewRequestView> { const r = await this.client.post(`/api/papers/requests/${rid}/me/items/${iid}/form`, { details }, { suppressErrorToast: CREW_QUIET }); return r.data.data; }
  async crewNotNeeded(rid: string, iid: string, reason: string): Promise<CP.CrewRequestView> { const r = await this.client.post(`/api/papers/requests/${rid}/me/items/${iid}/not-needed`, { reason }, { suppressErrorToast: CREW_QUIET }); return r.data.data; }
  async crewUndo(rid: string, iid: string): Promise<CP.CrewRequestView> { const r = await this.client.delete(`/api/papers/requests/${rid}/me/items/${iid}`, { suppressErrorToast: CREW_QUIET }); return r.data.data; }
  async crewShare(rid: string, body: { expiresAt?: string; perItem?: Record<string, string> }): Promise<CP.CrewRequestView> { const r = await this.client.post(`/api/papers/requests/${rid}/me/share`, body, { suppressErrorToast: CREW_QUIET }); return r.data.data; }
  async crewChangeAccess(rid: string, iid: string, expiresAt: string): Promise<CP.CrewRequestView> { const r = await this.client.patch(`/api/papers/requests/${rid}/me/items/${iid}/access`, { expiresAt }, { suppressErrorToast: CREW_QUIET }); return r.data.data; }
  async crewWithdraw(rid: string, itemIds?: string[]): Promise<CP.CrewRequestView> { const r = await this.client.post(`/api/papers/requests/${rid}/me/withdraw`, itemIds ? { itemIds } : {}, { suppressErrorToast: CREW_QUIET }); return r.data.data; }
  // Skipper (owner or captain of the vessel; only requests they made).
  async paperRequestSets(): Promise<CP.RequestSet[]> { const r = await this.client.get('/api/papers/request-sets'); return r.data.data; }
  async skipperRequests(vid: string): Promise<CP.RequestSummary[]> { const r = await this.client.get(`/api/papers/vessels/${vid}/requests`, { suppressErrorToast: CREW_QUIET }); return r.data.data; }
  async skipperRequest(vid: string, rid: string): Promise<CP.RequestDetail> { const r = await this.client.get(`/api/papers/vessels/${vid}/requests/${rid}`, { suppressErrorToast: CREW_QUIET }); return r.data.data; }
  async skipperCreateRequest(vid: string, input: CP.RequestInput): Promise<CP.RequestDetail> { const r = await this.client.post(`/api/papers/vessels/${vid}/requests`, input, { suppressErrorToast: CREW_QUIET }); return r.data.data; }
  async skipperUpdateRequest(vid: string, rid: string, input: Omit<CP.RequestInput, 'recipients' | 'set'>): Promise<CP.RequestDetail> { const r = await this.client.patch(`/api/papers/vessels/${vid}/requests/${rid}`, input, { suppressErrorToast: CREW_QUIET }); return r.data.data; }
  async skipperSendRequest(vid: string, rid: string): Promise<CP.RequestDetail> { const r = await this.client.post(`/api/papers/vessels/${vid}/requests/${rid}/send`, {}, { suppressErrorToast: CREW_QUIET }); return r.data.data; }
  async skipperCancelRequest(vid: string, rid: string): Promise<CP.RequestDetail> { const r = await this.client.post(`/api/papers/vessels/${vid}/requests/${rid}/cancel`, {}, { suppressErrorToast: CREW_QUIET }); return r.data.data; }
  async skipperAddRecipient(vid: string, rid: string, input: CP.RecipientInput): Promise<CP.RequestDetail> { const r = await this.client.post(`/api/papers/vessels/${vid}/requests/${rid}/recipients`, input, { suppressErrorToast: CREW_QUIET }); return r.data.data; }
  async skipperUpdateRecipient(vid: string, rid: string, recId: string, input: { name?: string | null; items?: CP.ItemInput[] }): Promise<CP.RequestDetail> { const r = await this.client.patch(`/api/papers/vessels/${vid}/requests/${rid}/recipients/${recId}`, input, { suppressErrorToast: CREW_QUIET }); return r.data.data; }
  async skipperRemoveRecipient(vid: string, rid: string, recId: string): Promise<CP.RequestDetail> { const r = await this.client.delete(`/api/papers/vessels/${vid}/requests/${rid}/recipients/${recId}`, { suppressErrorToast: CREW_QUIET }); return r.data.data; }
  async skipperResendLink(vid: string, rid: string, recId: string): Promise<CP.RequestDetail> { const r = await this.client.post(`/api/papers/vessels/${vid}/requests/${rid}/recipients/${recId}/resend`, {}, { suppressErrorToast: CREW_QUIET }); return r.data.data; }
  /** Opening a granted paper is a view in the crew member's activity. */
  async skipperGrantedPaper(vid: string, rid: string, iid: string): Promise<CP.GrantedPaper> { const r = await this.client.get(`/api/papers/vessels/${vid}/requests/${rid}/items/${iid}/paper`, { suppressErrorToast: CREW_QUIET }); return r.data.data; }
  // The tracker (Phase 3): remind now, ask about or accept a paper that needs a look, the crew list.
  async skipperRemindNow(vid: string, rid: string, recipientId?: string): Promise<CP.RequestDetail> { const r = await this.client.post(`/api/papers/vessels/${vid}/requests/${rid}/remind`, recipientId ? { recipientId } : {}, { suppressErrorToast: CREW_QUIET }); return r.data.data; }
  async skipperAskAbout(vid: string, rid: string, iid: string, note: string): Promise<CP.RequestDetail> { const r = await this.client.post(`/api/papers/vessels/${vid}/requests/${rid}/items/${iid}/ask`, { note }, { suppressErrorToast: CREW_QUIET }); return r.data.data; }
  async skipperAcceptAsIs(vid: string, rid: string, iid: string): Promise<CP.RequestDetail> { const r = await this.client.post(`/api/papers/vessels/${vid}/requests/${rid}/items/${iid}/accept`, {}, { suppressErrorToast: CREW_QUIET }); return r.data.data; }
  /** The crew list for clearance — each passport used is logged as an export in its owner's activity. */
  async skipperCrewList(vid: string, rid: string, format: 'pdf' | 'csv'): Promise<{ blob: Blob; fileName: string }> {
    const r = await this.client.get(`/api/papers/vessels/${vid}/requests/${rid}/crew-list`, { params: { format }, responseType: 'blob', timeout: BLOB_TIMEOUT, suppressErrorToast: CREW_QUIET });
    const m = /filename="([^"]+)"/.exec(String(r.headers?.['content-disposition'] ?? ''));
    return { blob: r.data as Blob, fileName: m?.[1] ?? `crew-list.${format}` };
  }
  /** A view streams the file; a download (download=true) is a copy stamped with who it was shared with, and when. */
  async skipperGrantedFileBlob(vid: string, rid: string, iid: string, fileId: string, download = false): Promise<Blob> {
    const r = await this.client.get(`/api/papers/vessels/${vid}/requests/${rid}/items/${iid}/paper/file`, { params: { fileId, ...(download ? { download: 1 } : {}) }, responseType: 'blob', timeout: BLOB_TIMEOUT, suppressErrorToast: CREW_QUIET });
    return r.data as Blob;
  }

  async navLibMine(): Promise<import('../types/navLibraries').NavLibrary[]> {
    const response = await this.client.get('/api/nav-libraries/mine');
    return response.data.data;
  }

  async navLibSubscribed(): Promise<import('../types/navLibraries').SubscribedLibrary[]> {
    const response = await this.client.get('/api/nav-libraries/subscribed');
    return response.data.data;
  }

  async navLibCreate(input: import('../types/navLibraries').LibraryInput): Promise<import('../types/navLibraries').NavLibrary> {
    const response = await this.client.post('/api/nav-libraries', input, { suppressErrorToast: [400] });
    return response.data.data;
  }

  async navLibUpdate(id: string, patch: Partial<import('../types/navLibraries').LibraryInput>): Promise<import('../types/navLibraries').NavLibrary> {
    const response = await this.client.patch(`/api/nav-libraries/${id}`, patch, { suppressErrorToast: [400] });
    return response.data.data;
  }

  async navLibDelete(id: string): Promise<void> {
    await this.client.delete(`/api/nav-libraries/${id}`);
  }

  async navLibDetail(id: string): Promise<import('../types/navLibraries').LibraryDetail> {
    const response = await this.client.get(`/api/nav-libraries/${id}`);
    return response.data.data;
  }

  async navLibAddItems(id: string, elementIds: string[]): Promise<{ added: number; skipped: number }> {
    const response = await this.client.post(`/api/nav-libraries/${id}/items`, { element_ids: elementIds }, { suppressErrorToast: [400, 403] });
    return response.data.data;
  }

  async navLibRemoveItem(id: string, elementId: string): Promise<void> {
    await this.client.delete(`/api/nav-libraries/${id}/items/${elementId}`);
  }

  async navLibMembers(id: string): Promise<import('../types/navLibraries').LibraryMember[]> {
    const response = await this.client.get(`/api/nav-libraries/${id}/members`);
    return response.data.data;
  }

  async navLibDecide(id: string, userId: string, status: 'approved' | 'declined' | 'revoked'): Promise<void> {
    await this.client.post(`/api/nav-libraries/${id}/members/${userId}`, { status });
  }

  async navLibRequest(id: string, message?: string): Promise<{ status: import('../types/navLibraries').MemberStatus }> {
    const response = await this.client.post(`/api/nav-libraries/${id}/request`, { message: message ?? null }, { suppressErrorToast: [400, 409] });
    return response.data.data;
  }

  async navLibLeave(id: string): Promise<void> {
    await this.client.post(`/api/nav-libraries/${id}/leave`, {});
  }

  async navLibCopy(id: string, elementIds: string[]): Promise<{ copied: number; ids: string[] }> {
    const response = await this.client.post(`/api/nav-libraries/${id}/copy`, { element_ids: elementIds }, { suppressErrorToast: [400, 403, 404] });
    return response.data.data;
  }

  async navLibPublishedIn(elementIds: string[]): Promise<Array<{ elementId: string; libraryId: string; name: string }>> {
    if (!elementIds.length) return [];
    const response = await this.client.post('/api/nav-libraries/published-in', { element_ids: elementIds });
    return response.data.data;
  }

  // ==========================================
  // Polaris Track (public vessel tracking) — owner side
  // ==========================================

  // ---- Google Drive charts (public shared-folder links; gated on packages.google_drive_charts) ----
  async driveFoldersAccess(): Promise<{ enabled: boolean }> {
    const response = await this.client.get('/api/drive-folders/access');
    return response.data.data;
  }

  async driveFoldersList(): Promise<import('../types/driveFolders').DriveChartFolder[]> {
    const response = await this.client.get('/api/drive-folders');
    return response.data.data;
  }

  /** 400/409/502 carry a user-facing message the page shows inline — no global toast. */
  async driveFoldersAdd(url: string): Promise<import('../types/driveFolders').DriveChartFolder> {
    const response = await this.client.post('/api/drive-folders', { url }, { suppressErrorToast: [400, 409, 502] });
    return response.data.data;
  }

  async driveFoldersRemove(id: string): Promise<void> {
    await this.client.delete(`/api/drive-folders/${id}`);
  }

  // ---- SD Card Manager (remote browse/delete of a Scout's SD card; gated on packages.sd_card_manager) ----
  async sdAccess(): Promise<{ enabled: boolean }> {
    const response = await this.client.get('/api/sd/access');
    return response.data.data;
  }

  async sdDevices(): Promise<import('../types/sdCard').SdDeviceSummary[]> {
    const response = await this.client.get('/api/sd/devices');
    return response.data.data;
  }

  async sdScan(deviceId: string): Promise<import('../types/sdCard').SdScanResponse> {
    const response = await this.client.get(`/api/sd/devices/${deviceId}/scan`);
    return response.data.data;
  }

  /** Page keep-alive: keeps the Scout polling fast and auto-rescans a stale card. */
  async sdSession(deviceId: string): Promise<{ interactiveUntil: string; scanQueued: boolean }> {
    const response = await this.client.post(`/api/sd/devices/${deviceId}/session`, {}, { suppressErrorToast: [403, 404] });
    return response.data.data;
  }

  async sdRescan(deviceId: string): Promise<{ command: import('../types/sdCard').SdCommand | null; already_pending: boolean }> {
    const response = await this.client.post(`/api/sd/devices/${deviceId}/rescan`, {});
    return response.data.data;
  }

  /** 400/403 carry a user-facing message the page shows inline — no global toast. */
  async sdDelete(deviceId: string, paths: string[]): Promise<{ command: import('../types/sdCard').SdCommand }> {
    const response = await this.client.post(`/api/sd/devices/${deviceId}/delete`, { paths }, { suppressErrorToast: [400, 403] });
    return response.data.data;
  }

  async sdCommands(deviceId: string): Promise<import('../types/sdCard').SdCommand[]> {
    const response = await this.client.get(`/api/sd/devices/${deviceId}/commands`);
    return response.data.data;
  }

  // ---- Anchor Watch remote view (the Scout's QR link, shown and shared from MyPolaris; gated on packages.anchor_remote_view) ----
  async anchorRemoteAccess(): Promise<{ enabled: boolean }> {
    const response = await this.client.get('/api/anchor-remote/access');
    return response.data.data;
  }

  async anchorRemoteDevices(): Promise<import('../types/anchorRemote').AnchorRemoteDevice[]> {
    const response = await this.client.get('/api/anchor-remote/devices');
    return response.data.data;
  }

  async trackingAccess(): Promise<{ enabled: boolean }> {
    const response = await this.client.get('/api/tracking/access');
    return response.data.data;
  }

  async trackingDevices(): Promise<import('../types/tracking').TrackingDevice[]> {
    const response = await this.client.get('/api/tracking/devices');
    return response.data.data;
  }

  async trackingUpdate(deviceId: string, patch: import('../types/tracking').TrackingPrefsPatch): Promise<void> {
    await this.client.patch(`/api/tracking/devices/${deviceId}`, patch);
  }

  async trackingNewPassage(deviceId: string): Promise<void> {
    await this.client.post(`/api/tracking/devices/${deviceId}/new-passage`);
  }

  async trackingLog(deviceId: string): Promise<import('../types/tracking').PassageLogEntry[]> {
    const response = await this.client.get(`/api/tracking/devices/${deviceId}/log`);
    return response.data.data;
  }

  async trackingAddLog(deviceId: string, body: string): Promise<import('../types/tracking').PassageLogEntry> {
    const response = await this.client.post(`/api/tracking/devices/${deviceId}/log`, { body });
    return response.data.data;
  }

  async trackingSuggestions(): Promise<import('../types/tracking').CourseSuggestion[]> {
    const response = await this.client.get('/api/tracking/suggestions');
    return response.data.data;
  }

  async trackingAcceptSuggestion(id: string, sendToDeviceId: string | null): Promise<{ element_id: string | null; sent_to: string | null; online: boolean }> {
    const response = await this.client.post(`/api/tracking/suggestions/${id}/accept`, { send_to_device_id: sendToDeviceId });
    return response.data.data;
  }

  async trackingDeclineSuggestion(id: string): Promise<void> {
    await this.client.post(`/api/tracking/suggestions/${id}/decline`);
  }

  // ==========================================
  // Admin basemap usage report
  // ==========================================

  async adminGetBasemapUsage(month: string): Promise<{
    month: string;
    users: {
      user_id: string;
      name: string | null;
      email: string;
      providers: Partial<Record<string, { browse: number; pack: number }>>;
      total_tiles: number;
    }[];
  }> {
    const response = await this.client.get(`/api/admin/usage/basemaps?month=${month}`);
    return response.data.data;
  }

  // ==========================================
  // Admin Billing Audit
  // ==========================================

  async adminGetBillingAudit(): Promise<Record<string, { status: 'ok' | 'warning'; count: number; issues: any[] }>> {
    const response = await this.client.get('/api/admin/billing/audit');
    return response.data.data;
  }

  async adminGetBillingAuditCheck(checkName: string): Promise<{ status: 'ok' | 'warning'; count: number; issues: any[] }> {
    const response = await this.client.get(`/api/admin/billing/audit/${checkName}`);
    return response.data.data;
  }
  // ==========================================
  // Admin Neptune Reports
  // ==========================================

  async adminGetNeptuneDevices(params?: {
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<{ devices: any[]; total: number; stats: any }> {
    const response = await this.client.get('/api/admin/neptune/devices', { params });
    return { devices: response.data.data || [], total: response.data.total || 0, stats: response.data.stats || {} };
  }

  async adminGetOtaBuilds(): Promise<any[]> {
    const response = await this.client.get('/api/admin/ota/builds');
    return response.data.data ?? [];
  }

  async adminGetOtaStatus(): Promise<any> {
    const response = await this.client.get('/api/admin/ota/status');
    return response.data.data;
  }

  async adminUpdateOtaBuildReleaseNotes(params: {
    channel: string;
    appDir: string;
    versionDir: string;
    packageId: string;
    releaseNotes: string;
  }): Promise<any> {
    const { channel, appDir, versionDir, packageId, releaseNotes } = params;
    const path = `/api/admin/ota/builds/${encodeURIComponent(channel)}/${encodeURIComponent(appDir)}/${encodeURIComponent(versionDir)}/${encodeURIComponent(packageId)}`;
    const response = await this.client.put(path, { releaseNotes });
    return response.data.data;
  }

  async adminPromoteOtaBuild(params: {
    channel: string;
    appDir: string;
    versionDir: string;
    packageId: string;
  }): Promise<any> {
    const { channel, appDir, versionDir, packageId } = params;
    const path = `/api/admin/ota/builds/${encodeURIComponent(channel)}/${encodeURIComponent(appDir)}/${encodeURIComponent(versionDir)}/${encodeURIComponent(packageId)}/promote`;
    const response = await this.client.post(path);
    return response.data.data;
  }

  async adminListOtaDeviceChannels(params?: { limit?: number; cursor?: string }): Promise<{
    data: import('../pages/admin/releases/data/deviceChannels').DeviceChannelEntry[];
    cursor: string | null;
    list_complete: boolean;
  }> {
    const response = await this.client.get('/api/admin/ota/device-channels', { params });
    return {
      data: response.data.data ?? [],
      cursor: response.data.cursor ?? null,
      list_complete: response.data.list_complete ?? true,
    };
  }

  async adminPutOtaDeviceChannel(
    deviceId: string,
    body: unknown,
  ): Promise<import('../pages/admin/releases/data/deviceChannels').DeviceChannelEntry> {
    const payload =
      typeof body === 'object' && body != null && !Array.isArray(body)
        ? { deviceId, ...(body as Record<string, unknown>) }
        : { deviceId, legacyChannel: body };
    const response = await this.client.post('/api/admin/ota/device-channels', payload, {
      suppressErrorToast: [400, 404, 409],
    });
    return response.data.data;
  }

  async adminDeleteOtaDeviceChannel(deviceId: string): Promise<void> {
    await this.client.delete(`/api/admin/ota/device-channels/${encodeURIComponent(deviceId)}`, {
      suppressErrorToast: [404],
    });
  }

  // ---- Scout App Library (third-party apps curated for the Scout) ----

  async adminAppLibraryList(): Promise<any[]> {
    const response = await this.client.get('/api/admin/app-library');
    return response.data.data ?? [];
  }

  async adminAppLibraryPlans(): Promise<Array<{ id: string; name: string; visibility: string | null }>> {
    const response = await this.client.get('/api/admin/app-library/plans');
    return response.data.data ?? [];
  }

  async adminAppLibraryCreateApp(data: Record<string, unknown>): Promise<any> {
    const response = await this.client.post('/api/admin/app-library/apps', data, { suppressErrorToast: [400, 409] });
    return response.data.data;
  }

  async adminAppLibraryUpdateApp(id: string, data: Record<string, unknown>): Promise<any> {
    const response = await this.client.patch(`/api/admin/app-library/apps/${encodeURIComponent(id)}`, data, { suppressErrorToast: [400, 404, 409] });
    return response.data.data;
  }

  async adminAppLibraryDeleteApp(id: string): Promise<void> {
    await this.client.delete(`/api/admin/app-library/apps/${encodeURIComponent(id)}`, { suppressErrorToast: [404, 409] });
  }

  async adminAppLibraryCreateVersion(appId: string, data: Record<string, unknown>): Promise<any> {
    const response = await this.client.post(`/api/admin/app-library/apps/${encodeURIComponent(appId)}/versions`, data, { suppressErrorToast: [400, 409, 413] });
    return response.data.data;
  }

  async adminAppLibraryStartUpload(versionId: string, fileName: string): Promise<{ upload_id: string; key: string; part_size: number }> {
    const response = await this.client.post(`/api/admin/app-library/versions/${encodeURIComponent(versionId)}/files/${encodeURIComponent(fileName)}/upload`);
    return response.data.data;
  }

  async adminAppLibraryUploadPart(versionId: string, fileName: string, uploadId: string, partNumber: number, body: Blob): Promise<{ part_number: number; etag: string }> {
    const response = await this.client.put(
      `/api/admin/app-library/versions/${encodeURIComponent(versionId)}/files/${encodeURIComponent(fileName)}/parts/${partNumber}`,
      body,
      { params: { upload_id: uploadId }, headers: { 'Content-Type': 'application/octet-stream' }, timeout: 10 * 60 * 1000 },
    );
    return response.data.data;
  }

  async adminAppLibraryCompleteUpload(versionId: string, fileName: string, uploadId: string, parts: Array<{ part_number: number; etag: string }>): Promise<any> {
    const response = await this.client.post(
      `/api/admin/app-library/versions/${encodeURIComponent(versionId)}/files/${encodeURIComponent(fileName)}/complete`,
      { upload_id: uploadId, parts },
      { suppressErrorToast: [409], timeout: 5 * 60 * 1000 },
    );
    return response.data.data;
  }

  async adminAppLibrarySetVersionState(versionId: string, state: string): Promise<any> {
    const response = await this.client.post(`/api/admin/app-library/versions/${encodeURIComponent(versionId)}/state`, { state }, { suppressErrorToast: [409] });
    return response.data.data;
  }

  async adminAppLibraryPatchVersion(versionId: string, data: { note?: string | null; required?: boolean }): Promise<any> {
    const response = await this.client.patch(`/api/admin/app-library/versions/${encodeURIComponent(versionId)}`, data);
    return response.data.data;
  }

  async adminAppLibraryDeleteVersion(versionId: string): Promise<void> {
    await this.client.delete(`/api/admin/app-library/versions/${encodeURIComponent(versionId)}`, { suppressErrorToast: [409] });
  }

  async adminGetNeptuneUpdates(params?: {
    page?: number;
    limit?: number;
    result?: string;
    device_id?: string;
  }): Promise<{ updates: any[]; total: number }> {
    const response = await this.client.get('/api/admin/neptune/updates', { params });
    return { updates: response.data.data || [], total: response.data.total || 0 };
  }

  // ---- Admin › Update Checks › Live monitor (read-only feeds) ----

  /** ota-updater worker requests in [from, to] (ms). Window is clamped to 1 h server-side.
   *  Resolves { configured: false } when the worker lacks the observability token. */
  async adminOtaLiveRequests(params: { from: number; to: number }): Promise<{ configured: boolean; message?: string; data: OtaLiveRequest[] }> {
    try {
      const response = await this.client.get('/api/admin/ota/live/requests', { params });
      return { configured: true, data: response.data.data || [] };
    } catch (err: unknown) {
      const e = err as { response?: { status?: number; data?: { error?: string; message?: string } } };
      if (e.response?.status === 503 && e.response.data?.error === 'not_configured') {
        return { configured: false, message: e.response.data.message, data: [] };
      }
      throw err;
    }
  }

  /** Device "check for updates" reports received after `since` (ISO), oldest first. */
  async adminOtaLiveReports(params?: { since?: string; limit?: number }): Promise<{ data: OtaLiveReport[]; total: number }> {
    const response = await this.client.get('/api/admin/ota/live/reports', { params });
    return { data: response.data.data || [], total: response.data.total || 0 };
  }

  /** When each app version was first offered to any Scout (admin device page release dates). */
  async adminOtaReleaseDates(): Promise<Array<{ package: string; versionCode: number; versionName: string; firstOfferedAt: string }>> {
    const response = await this.client.get('/api/admin/ota/live/release-dates');
    return Array.isArray(response.data?.data) ? response.data.data : [];
  }

  /** Admin › OTA Releases › Changelog: every OTA since release notes began, with its notes. */
  async adminGetOtaChangelog(): Promise<import('../pages/admin/releases/data/changelog').OtaChangelogResponse> {
    const response = await this.client.get('/api/admin/ota/changelog');
    return response.data;
  }

  /** Admin › OTA Releases › Register updates: every release with drafted register changes. */
  async adminRegisterReleases(): Promise<import('../pages/admin/releases/data/register').RegisterRelease[]> {
    const response = await this.client.get('/api/admin/register-updates/releases');
    return response.data?.data?.releases ?? [];
  }

  /** One release and its drafted changes, in order. */
  async adminRegisterRelease(id: string): Promise<import('../pages/admin/releases/data/register').RegisterReleaseDetail> {
    const response = await this.client.get(`/api/admin/register-updates/releases/${encodeURIComponent(id)}`);
    return response.data.data;
  }

  /** Accept (with the columns to write), reject or reopen one change. */
  async adminRegisterDecide(id: string, body: import('../pages/admin/releases/data/register').RegisterDecision): Promise<import('../pages/admin/releases/data/register').RegisterChange> {
    const response = await this.client.post(`/api/admin/register-updates/changes/${encodeURIComponent(id)}/decision`, body);
    return response.data.data;
  }

  /** One call on a bundle of changes: accept takes each as proposed. */
  async adminRegisterDecideMany(body: { ids: string[]; action: 'accept' | 'reject'; note?: string | null }): Promise<{ changes: import('../pages/admin/releases/data/register').RegisterChange[]; failed: { id: string; error: string }[] }> {
    const response = await this.client.post('/api/admin/register-updates/changes/decisions', body);
    return response.data.data;
  }

  /** Admin › Help: the help's sections, in order. */
  async adminHelpSections(): Promise<import('../pages/admin/help/data/help').HelpSection[]> {
    const response = await this.client.get('/api/admin/help/sections');
    return response.data?.data?.sections ?? [];
  }

  async adminHelpCreateSection(body: { slug: string; title: string; icon: string }): Promise<import('../pages/admin/help/data/help').HelpSection> {
    const response = await this.client.post('/api/admin/help/sections', body);
    return response.data.data;
  }

  /** Every help article with its newest revision, live revision number and open flags. */
  async adminHelpArticles(): Promise<import('../pages/admin/help/data/help').HelpArticleRow[]> {
    const response = await this.client.get('/api/admin/help/articles');
    return response.data?.data?.articles ?? [];
  }

  /** An article with one revision in full (the newest, or `revision`), its checks, checklist and device checks. */
  async adminHelpArticle(id: string, revision?: string): Promise<import('../pages/admin/help/data/help').HelpArticleDetail> {
    const response = await this.client.get(`/api/admin/help/articles/${encodeURIComponent(id)}`, { params: revision ? { revision } : undefined });
    return response.data.data;
  }

  /** Saves a new draft revision on top of `basedOn`, the newest (409 otherwise). */
  async adminHelpSaveRevision(id: string, body: import('../pages/admin/help/data/help').RevisionBody): Promise<{ id: string; number: number }> {
    const response = await this.client.post(`/api/admin/help/articles/${encodeURIComponent(id)}/revisions`, body);
    return response.data.data;
  }

  /** Draft → ready for check; a 422 carries the failing checks. */
  async adminHelpMarkReady(revisionId: string): Promise<{ id: string; state: string }> {
    const response = await this.client.post(`/api/admin/help/revisions/${encodeURIComponent(revisionId)}/ready`, {});
    return response.data.data;
  }

  /** Records a device check of a ready revision; a pass moves it to checked. */
  async adminHelpDeviceCheck(revisionId: string, body: import('../pages/admin/help/data/help').DeviceCheckBody): Promise<{ result: 'pass' | 'fail'; deviceName: string }> {
    const response = await this.client.post(`/api/admin/help/revisions/${encodeURIComponent(revisionId)}/device-checks`, body);
    return response.data.data;
  }

  /** Closes a flag: resolved (the article was changed for it) or no_change; 409 if already closed. */
  async adminHelpResolveFlag(id: string, body: { status: 'resolved' | 'no_change'; reason: string | null }): Promise<import('../pages/admin/help/data/help').HelpFlag> {
    const response = await this.client.post(`/api/admin/help/flags/${encodeURIComponent(id)}/resolve`, body, { suppressErrorToast: [409] });
    return response.data.data;
  }

  /** Every uploaded help image and clip, newest first. */
  async adminHelpMedia(): Promise<import('../pages/admin/help/data/help').HelpMedia[]> {
    const response = await this.client.get('/api/admin/help/media');
    return response.data?.data?.media ?? [];
  }

  /** Uploads an image, or a GIF clip with its still frame. The same bytes again come back as the stored copy, `reused`. */
  async adminHelpUploadMedia(u: import('../pages/admin/help/data/help').MediaUpload): Promise<{ media: import('../pages/admin/help/data/help').HelpMedia; reused: boolean }> {
    const fd = new FormData();
    fd.append('kind', u.kind);
    fd.append('file', u.file);
    if (u.still) fd.append('still', u.still, 'still');
    fd.append('alt', u.alt);
    fd.append('noPersonalData', String(u.noPersonalData));
    if (u.takenOn) { fd.append('takenOnApp', u.takenOn.app); fd.append('takenOnVersion', u.takenOn.version); }
    const response = await this.client.post('/api/admin/help/media', fd, {
      headers: { 'Content-Type': 'multipart/form-data' }, timeout: BLOB_TIMEOUT, suppressErrorToast: [400, 413, 501, 503],
    });
    return response.data.data;
  }

  /** Alt text, the no-personal-data tick and the software an image was taken on. */
  async adminHelpUpdateMedia(id: string, patch: import('../pages/admin/help/data/help').MediaPatch): Promise<import('../pages/admin/help/data/help').HelpMedia> {
    const response = await this.client.patch(`/api/admin/help/media/${encodeURIComponent(id)}`, patch, { suppressErrorToast: [400, 404] });
    return response.data.data;
  }

  /** For <img>: the session cookie rides along via CORS credentials, as for screenshots. */
  adminHelpMediaUrl(id: string, still = false): string {
    return `${API_BASE_URL}/api/admin/help/media/${encodeURIComponent(id)}/file${still ? '?part=still' : ''}`;
  }

  /** A new article and its first revision, a draft. */
  async adminHelpCreateArticle(body: import('../pages/admin/help/data/help').NewArticleInput): Promise<{ article: { id: string } }> {
    const response = await this.client.post('/api/admin/help/articles', body);
    return response.data.data;
  }

  /** Admin › Changelog › Platform: what each part of the platform deployed, and when. */
  async adminGetPlatformChangelog(): Promise<import('../pages/admin/releases/data/platform').PlatformChangelogResponse> {
    const response = await this.client.get('/api/admin/changelog/platform');
    return response.data;
  }

  /** What each channel is offering right now, plus the app id → name map. */
  async adminOtaLiveManifests(): Promise<{ apps: Record<string, { pkg: string; name: string }>; manifests: Record<'stable' | 'platform', OtaLiveManifest | null> }> {
    const response = await this.client.get('/api/admin/ota/live/manifests');
    return { apps: response.data.apps || {}, manifests: response.data.manifests || { stable: null, platform: null } };
  }

  // ---- Admin › Weather ----

  /** Catalog + the weather worker's log summary for the last `hours`. */
  async adminWeatherOverview(hours = 24): Promise<WeatherOverview> {
    const response = await this.client.get('/api/admin/weather/overview', { params: { hours } });
    return response.data as WeatherOverview;
  }

  /** R2 listing of one run and region against the catalog. */
  async adminWeatherAvailability(params: { model: string; run: string; region: string }): Promise<WeatherAvailabilityResult> {
    const response = await this.client.get('/api/admin/weather/availability', { params });
    return response.data as WeatherAvailabilityResult;
  }

  /** How complete each model's worldwide tiles are, for its newest four runs. */
  async adminWeatherTiles(): Promise<WeatherTilesResult> {
    const response = await this.client.get('/api/admin/weather/tiles');
    return response.data as WeatherTilesResult;
  }

  /** Ask the weather worker to ingest now. The API holds the trigger secret. */
  async adminWeatherIngest(params: WeatherTriggerParams): Promise<{ success: boolean; upstream: unknown }> {
    const response = await this.client.post('/api/admin/weather/ingest', params);
    return response.data;
  }

  // ---- Admin › Routing ----

  /** Run the passage-planning engine: a route, a comparison of models or a departure window. */
  async adminRoutingSubmit(form: RoutingForm): Promise<RoutingJobStatus> {
    const response = await this.client.post('/api/admin/routing/jobs', form);
    return response.data as RoutingJobStatus;
  }

  /** Read a polar file, whatever its layout (the API recognises it): text as text, any
   *  other file as its bytes in base64. A file that cannot be used answers 422 with its
   *  issues, returned here like a success. */
  async adminRoutingPolar(fileName: string, content: string | { base64: string }): Promise<RoutingPolarImport> {
    try {
      const file = typeof content === 'string' ? { text: content } : { base64: content.base64 };
      const response = await this.client.post('/api/admin/routing/polar', { fileName, ...file });
      return response.data as RoutingPolarImport;
    } catch (e) {
      const data = (e as { response?: { status?: number; data?: RoutingPolarImport } }).response;
      if (data?.status === 422 && data.data) return data.data;
      throw e;
    }
  }

  /** The boat library: curated polars to choose instead of a file. */
  async polarLibrary(): Promise<PolarLibraryEntry[]> {
    const response = await this.client.get('/api/polars/library');
    return (response.data.data ?? []) as PolarLibraryEntry[];
  }

  /** One library polar, in the shape a polar file's answer has. */
  async polarLibraryPolar(id: string): Promise<RoutingPolarImport> {
    const response = await this.client.get(`/api/polars/library/${encodeURIComponent(id)}`);
    return response.data as RoutingPolarImport;
  }

  /** Admin › Routing: the engine's health and every run in the last `hours`. */
  async adminRoutingOverview(hours = 48): Promise<{ health: RoutingHealth; jobs: RoutingConsoleJob[] }> {
    const response = await this.client.get('/api/admin/routing/overview', { params: { hours } });
    return { health: response.data.health as RoutingHealth, jobs: (response.data.jobs ?? []) as RoutingConsoleJob[] };
  }

  /** What each run in the last `hours` cost on Cloudflare, or why that cannot be said. */
  async adminRoutingCosts(hours = 48): Promise<RoutingCosts> {
    const response = await this.client.get('/api/admin/routing/costs', { params: { hours } });
    const { success: _success, ...costs } = response.data as RoutingCosts & { success?: boolean };
    return costs as RoutingCosts;
  }

  /** Run a job again (stopping it first if it runs); the id of the run that follows. */
  async adminRoutingRestart(id: string): Promise<{ id: string; state: RoutingJobState }> {
    const response = await this.client.post(`/api/admin/routing/jobs/${id}/restart`);
    return response.data as { id: string; state: RoutingJobState };
  }

  /**
   * Delete a finished run's record and result. Without `force`, a run a departure check or
   * passage commentary still reads is refused: `{ deleted: false, uses }`.
   */
  async adminRoutingDelete(id: string, force = false): Promise<{ deleted: true } | { deleted: false; uses: RoutingInUse }> {
    try {
      await this.client.delete(`/api/admin/routing/jobs/${id}`, { params: force ? { force: 1 } : {}, suppressErrorToast: [409] });
      return { deleted: true };
    } catch (e) {
      const r = (e as { response?: { status?: number; data?: { error?: string; uses?: RoutingInUse } } }).response;
      if (r?.status === 409 && r.data?.error === 'in_use' && r.data.uses) return { deleted: false, uses: r.data.uses };
      throw e;
    }
  }

  /** Stop a job for good (it is never sent again). */
  async adminRoutingCancel(id: string): Promise<void> {
    await this.client.post(`/api/admin/routing/jobs/${id}/cancel`);
  }

  /** Move a waiting job to the front of the queue (it runs next) or the back (it runs last). */
  async adminRoutingMove(id: string, to: 'front' | 'back'): Promise<void> {
    await this.client.post(`/api/admin/routing/jobs/${id}/move`, { to });
  }

  async adminRoutingStatus(id: string): Promise<RoutingJobStatus> {
    const response = await this.client.get(`/api/admin/routing/jobs/${id}`);
    return response.data as RoutingJobStatus;
  }

  async adminRoutingResult(id: string): Promise<RoutingResult> {
    const response = await this.client.get(`/api/admin/routing/jobs/${id}/result`);
    return response.data as RoutingResult;
  }

  // ---- Admin › Departure planner ----

  /** Every departure watch, newest first. */
  async adminDepartureWatches(): Promise<DepartureWatch[]> {
    const response = await this.client.get('/api/admin/departure-watches');
    return (response.data.data ?? []) as DepartureWatch[];
  }

  async adminDepartureWatchCreate(form: DepartureWatchForm): Promise<DepartureWatch> {
    const response = await this.client.post('/api/admin/departure-watches', form, { suppressErrorToast: [400] });
    return response.data.data as DepartureWatch;
  }

  async adminDepartureWatchUpdate(id: string, form: DepartureWatchForm): Promise<DepartureWatch> {
    const response = await this.client.patch(`/api/admin/departure-watches/${id}`, form, { suppressErrorToast: [400] });
    return response.data.data as DepartureWatch;
  }

  async adminDepartureWatchDelete(id: string): Promise<void> {
    await this.client.delete(`/api/admin/departure-watches/${id}`);
  }

  async adminDepartureWatchStatus(id: string, status: DepartureWatchStatus): Promise<DepartureWatch> {
    const response = await this.client.post(`/api/admin/departure-watches/${id}/status`, { status });
    return response.data.data as DepartureWatch;
  }

  /** A watch's assessments, newest first, drafts included. */
  async adminDepartureAssessments(watchId: string): Promise<DepartureAssessment[]> {
    const response = await this.client.get(`/api/admin/departure-watches/${watchId}/assessments`);
    return (response.data.data ?? []) as DepartureAssessment[];
  }

  /** Approve a draft, with the reviewer's edits; it goes out at its send time. */
  async adminDepartureAssessmentApprove(id: string, edits: { subject?: string; body?: string } = {}): Promise<DepartureAssessment> {
    const response = await this.client.post(`/api/admin/departure-watches/assessments/${id}/approve`, edits, { suppressErrorToast: [409] });
    return response.data.data as DepartureAssessment;
  }

  /**
   * "Assess now": a full assessment of the watch at once, without the testers' limits.
   * The new assessment (`{ id, stage, ... }`, 202), or `{ unchanged, message }` when the
   * forecasts and settings are as last time. 409 (running, ended, planner off) throws.
   */
  async adminDepartureWatchAssess(id: string): Promise<{ id?: string; stage?: string; unchanged?: boolean; message?: string }> {
    const response = await this.client.post(`/api/admin/departure-watches/${id}/assess`, {}, { suppressErrorToast: [409, 502, 503] });
    return (response.data.data ?? {}) as { id?: string; stage?: string; unchanged?: boolean; message?: string };
  }

  /** The one-off port time-zone fix: a dry run lists the watches, `apply` sets them. */
  async adminDepartureTimeZones(apply: boolean): Promise<{ fixes: { id: string; label: string; from: string; to: string; port: string }[]; applied: number }> {
    const response = await this.client.post('/api/admin/departure-watches/time-zones', { apply });
    return response.data.data;
  }

  /** The test-fleet requests from router.vortexnav.com's landing page, newest first. */
  async adminRouterTesterRequests(): Promise<RouterTesterRequest[]> {
    const response = await this.client.get('/api/admin/router-tester-requests');
    return (response.data.data ?? []) as RouterTesterRequest[];
  }

  /** Emails the skipper how to sign in to WindPro, and marks the request invited. */
  async adminRouterTesterInvite(id: string): Promise<RouterTesterRequest> {
    const response = await this.client.post(`/api/admin/router-tester-requests/${id}/invite`, {}, { suppressErrorToast: [404, 502] });
    return response.data.data as RouterTesterRequest;
  }

  async adminDepartureAssessmentHold(id: string): Promise<DepartureAssessment> {
    const response = await this.client.post(`/api/admin/departure-watches/assessments/${id}/hold`, {}, { suppressErrorToast: [409] });
    return response.data.data as DepartureAssessment;
  }

  async adminGetNeptuneFeedback(params?: {
    page?: number;
    limit?: number;
    status?: string;
    category?: string;
    device_id?: string;
  }): Promise<{ feedback: any[]; total: number }> {
    const response = await this.client.get('/api/admin/neptune/feedback', { params });
    return { feedback: response.data.data || [], total: response.data.total || 0 };
  }

  async adminUpdateNeptuneFeedbackStatus(id: string, data: { status: string; notes?: string }): Promise<void> {
    await this.client.post(`/api/admin/neptune/feedback/${id}/status`, data);
  }

  // ---- admin: Feedback console (reports sent from Scouts) ----
  async adminFeedbackList(params: FeedbackListParams = {}): Promise<FeedbackListPage> {
    const response = await this.client.get('/api/admin/feedback', { params });
    return response.data.data as FeedbackListPage;
  }

  /** The people who can own a report. */
  async adminFeedbackStaff(): Promise<FeedbackStaffMember[]> {
    const response = await this.client.get('/api/admin/feedback/staff');
    return response.data.data as FeedbackStaffMember[];
  }

  async adminFeedback(id: string): Promise<FeedbackDetail> {
    const response = await this.client.get(`/api/admin/feedback/${id}`, { suppressErrorToast: [404] });
    return response.data.data as FeedbackDetail;
  }

  async adminFeedbackUpdate(id: string, input: FeedbackUpdateInput): Promise<FeedbackDetail> {
    const response = await this.client.patch(`/api/admin/feedback/${id}`, input);
    return response.data.data as FeedbackDetail;
  }

  /** `customer` shows on the customer's Scout; `internal` is for staff only. */
  async adminFeedbackComment(id: string, input: { body: string; visibility: FeedbackVisibility }): Promise<FeedbackEvent> {
    const response = await this.client.post(`/api/admin/feedback/${id}/comments`, input);
    return response.data.data as FeedbackEvent;
  }

  async adminFeedbackDelete(id: string): Promise<void> {
    await this.client.delete(`/api/admin/feedback/${id}`);
  }

  // ==========================================
  // Admin - Stock Inventory
  // ==========================================

  async adminGetInventoryStockStats(): Promise<StockInventoryStats> {
    const response = await this.client.get('/api/admin/inventory/stats');
    return response.data.data;
  }

  async adminListStockProducts(includeInactive = false): Promise<StockProduct[]> {
    const response = await this.client.get('/api/admin/inventory/products', {
      params: includeInactive ? { include_inactive: 'true' } : {},
    });
    return response.data.data;
  }

  async adminCreateStockProduct(data: {
    sku: string;
    name: string;
    manufacturer?: string | null;
    category: ProductCategory;
    hasBuildStep?: boolean;
    intakeParser?: IntakeParser;
    attributesSchema?: AttributeSchemaField[];
  }): Promise<StockProduct> {
    const response = await this.client.post('/api/admin/inventory/products', data);
    return response.data.data;
  }

  async adminUpdateStockProduct(id: string, data: {
    name?: string;
    manufacturer?: string | null;
    category?: ProductCategory;
    hasBuildStep?: boolean;
    intakeParser?: IntakeParser;
    attributesSchema?: AttributeSchemaField[];
    isActive?: boolean;
  }): Promise<StockProduct> {
    const response = await this.client.put(`/api/admin/inventory/products/${id}`, data);
    return response.data.data;
  }

  async adminDeleteStockProduct(id: string): Promise<void> {
    await this.client.delete(`/api/admin/inventory/products/${id}`);
  }

  async adminListStockItems(params?: {
    page?: number;
    limit?: number;
    state?: StockState;
    product_id?: string;
    batch_tag?: string;
    search?: string;
    missing_mac?: boolean;
  }): Promise<{ data: StockItem[]; pagination: { page: number; limit: number; total: number; totalPages: number } }> {
    const response = await this.client.get('/api/admin/inventory/items', { params });
    return response.data;
  }

  async adminGetStockItem(id: string): Promise<StockItemDetail> {
    const response = await this.client.get(`/api/admin/inventory/items/${id}`);
    return response.data.data;
  }

  async adminGetStockItemByMac(mac: string): Promise<StockItem> {
    const response = await this.client.get(`/api/admin/inventory/items/by-mac/${encodeURIComponent(mac)}`);
    return response.data.data;
  }

  async adminUpdateStockItem(id: string, data: {
    wifi_mac_address?: string | null;
    notes?: string | null;
  }): Promise<StockItem> {
    const response = await this.client.patch(`/api/admin/inventory/items/${id}`, data);
    return response.data.data;
  }

  async adminReceiveStockItem(data: {
    product_id: string;
    raw_scan?: string;
    serial_number?: string;
    batch_tag?: string | null;
    wifi_mac_address?: string | null;
    attributes?: Record<string, string>;
    notes?: string | null;
  }): Promise<StockItem> {
    const response = await this.client.post('/api/admin/inventory/receive', data);
    return response.data.data;
  }

  async adminReceiveStockBatch(data: {
    product_id: string;
    batch_tag?: string | null;
    items: { raw_scan?: string; serial_number?: string; wifi_mac_address?: string | null; attributes?: Record<string, string>; notes?: string | null }[];
  }): Promise<ReceiveBatchResult> {
    const response = await this.client.post('/api/admin/inventory/receive-batch', data);
    return response.data.data;
  }

  async adminTransitionStockState(id: string, toState: StockState, notes?: string): Promise<StockItem> {
    const response = await this.client.post(`/api/admin/inventory/items/${id}/transition`, { to_state: toState, notes });
    return response.data.data;
  }

  async adminBulkTransitionStockItems(itemIds: string[], toState: StockState, notes?: string): Promise<{
    results: { id: string; ok: boolean; error?: string }[];
    succeeded: number;
    failed: number;
  }> {
    const response = await this.client.post('/api/admin/inventory/items/bulk-transition', {
      item_ids: itemIds,
      to_state: toState,
      notes,
    });
    return response.data.data;
  }

  async adminRegisterStockBuild(id: string, data: {
    built_by?: string | null;
    app_versions?: Record<string, string>;
    git_sha?: string | null;
    android_version?: string | null;
    notes?: string | null;
  }): Promise<StockBuild> {
    const response = await this.client.post(`/api/admin/inventory/items/${id}/build`, data);
    return response.data.data;
  }

  async adminAllocateStockItem(id: string, data: { order_id?: string; notes?: string }): Promise<StockAllocation> {
    const response = await this.client.post(`/api/admin/inventory/items/${id}/allocate`, data);
    return response.data.data;
  }

  async adminShipStockItem(id: string, data: { tracking_ref?: string; carrier?: string; notes?: string }): Promise<StockAllocation> {
    const response = await this.client.post(`/api/admin/inventory/items/${id}/ship`, data);
    return response.data.data;
  }

  // ==========================================
  // Hardware orders (Shopify-ingested)
  // ==========================================

  async adminListHardwareOrders(params?: {
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<{
    orders: HardwareOrder[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
  }> {
    const response = await this.client.get('/api/admin/hardware-orders', { params });
    return response.data;
  }

  async adminGetHardwareOrder(id: string): Promise<HardwareOrder> {
    const response = await this.client.get(`/api/admin/hardware-orders/${id}`);
    return response.data;
  }

  // ==========================================
  // Departments + staff timesheeting
  // ==========================================

  // Departments — list is available to any authenticated user (staff need
  // active departments to book against); mutations are admin-only.
  async listDepartments(includeArchived = false): Promise<Department[]> {
    const response = await this.client.get('/api/departments', {
      params: includeArchived ? { includeArchived: 'true' } : undefined,
    });
    return response.data.data || [];
  }

  async adminCreateDepartment(data: { name: string; sortOrder?: number }): Promise<Department> {
    const response = await this.client.post('/api/departments', data);
    return response.data.data;
  }

  async adminUpdateDepartment(id: string, data: { name?: string; status?: DepartmentStatus; sortOrder?: number }): Promise<Department> {
    const response = await this.client.put(`/api/departments/${id}`, data);
    return response.data.data;
  }

  async adminArchiveDepartment(id: string): Promise<Department> {
    const response = await this.client.delete(`/api/departments/${id}`);
    return response.data.data;
  }

  // Staff tagging (admin)
  async adminListStaff(): Promise<StaffMember[]> {
    const response = await this.client.get('/api/timesheets/staff');
    return response.data.data || [];
  }

  async adminSetStaff(userId: string, data: { isStaff: boolean; hourlyRateCents?: number | null }): Promise<StaffMember> {
    const response = await this.client.put(`/api/timesheets/staff/${userId}`, data);
    return response.data.data;
  }

  // Entries — staff get their own (no pay figures); admins get full rows.
  async listMyTimesheetEntries(params?: { from?: string; to?: string }): Promise<MyTimesheetEntry[]> {
    const response = await this.client.get('/api/timesheets/entries', { params });
    return response.data.data || [];
  }

  async adminListTimesheetEntries(params?: { userId?: string; departmentId?: string; from?: string; to?: string }): Promise<TimesheetEntry[]> {
    const response = await this.client.get('/api/timesheets/entries', { params });
    return response.data.data || [];
  }

  async createTimesheetEntry(data: CreateTimesheetEntryInput): Promise<TimesheetEntry> {
    const response = await this.client.post('/api/timesheets/entries', data);
    return response.data.data;
  }

  async updateTimesheetEntry(id: string, data: UpdateTimesheetEntryInput): Promise<TimesheetEntry> {
    const response = await this.client.put(`/api/timesheets/entries/${id}`, data);
    return response.data.data;
  }

  async deleteTimesheetEntry(id: string): Promise<void> {
    await this.client.delete(`/api/timesheets/entries/${id}`);
  }

  // Reports (admin)
  async adminTimesheetReportByDepartment(params?: { from?: string; to?: string }): Promise<DepartmentReportRow[]> {
    const response = await this.client.get('/api/timesheets/reports/by-department', { params });
    return response.data.data || [];
  }

  async adminTimesheetReportByStaff(params?: { from?: string; to?: string }): Promise<StaffReportRow[]> {
    const response = await this.client.get('/api/timesheets/reports/by-staff', { params });
    return response.data.data || [];
  }

  async adminTimesheetReportMatrix(params?: { from?: string; to?: string }): Promise<MatrixReportRow[]> {
    const response = await this.client.get('/api/timesheets/reports/matrix', { params });
    return response.data.data || [];
  }
}

// Export singleton instance
export const api = new ApiService();

export default api;

/** One tap on "I Understand" in PolarisOS's shared safety warning. */
export interface SafetyAcceptance {
  id: string;
  /** When it was accepted (the Scout's clock, ISO). */
  accepted_at: string;
  /** Package of the app it was accepted in, e.g. com.vortexmarine.vortexnav. */
  app: string | null;
  /** Version of the warning's wording. */
  version: number | null;
  /** Which restart it covered, e.g. "count:86". */
  boot_id: string | null;
  /** When the platform received it. */
  recorded_at: string;
}
